(() => {
  if (window.CliDynamicCatalogue?.mount) {
    window.CliDynamicCatalogue.mount();
    return;
  }

  const INDEX_VERSION = '2026-10-04-1';
  const CACHE_TTL = 10 * 60 * 1000;
  // The brands whose prices and basket are for signed-in trade customers
  // only. Decided by the product's vendor field alone: a Hikvision camera
  // that came through the Technomate feed is still a Hikvision camera.
  const RESTRICTED_PRICE_BRANDS = new Set(['ezviz', 'technomate', 'klear']);
  const GLOBAL_FACETS = new Set(['brand', 'area', 'family', 'availability', 'price']);
  // Every vendor that has published products must be listed here. The vendor
  // field is matched by exact equality first, so a listed vendor can never be
  // re-badged by the title/tag fallback below it — an unlisted one can, and
  // was: MikroTik's "Cloud Core Router" range badged as CORE because
  // 'cloud-core-router' contains 'core' and MikroTik itself was missing.
  // Sorted longest-first at load so the fallback prefers the most specific
  // name; the short, collision-prone ones (CORE, BCS, ZTE) end up last.
  const BRAND_NAMES = [
    'Allied Telesis', 'Western Digital', 'Hanwha Vision', 'OEM by Sansec', 'RF Elements',
    'SentinelOne', 'Grandstream', 'Technomate', 'Intellinet', 'QuWireless', 'Teltonika',
    'Hikvision', 'Ubiquiti', 'Fortinet', 'MikroTik', 'Synology', 'Starlink', 'DrayTek',
    'Netgear', 'Gigaset', 'Seagate', 'SIMPoYo', 'Uniview', 'DIGITUS', 'Fibrain', 'Pyronix',
    'Texecom', 'TP-Link', 'Yealink', 'GL.iNet', 'D-Link', 'HUAWEI', 'Neolink', 'PLANET',
    'Fanvil', 'HiLook', 'Techly', 'EZVIZ', 'Klear', 'Jabra', 'Dahua', 'Cisco', 'Aqara',
    'Zyxel', 'Cudy', 'Snom', 'Poly', 'QNAP', 'IMOU', 'ASUS', 'Dell', 'HPE', 'BCS', 'ZTE',
    'JPL', 'CORE'
  ].sort((a, b) => b.length - a.length || a.localeCompare(b));
  // Vendor spellings that are the same brand. Applied before detection so the
  // facet shows one entry, not three: the catalogue carries TP-LINK/TP-Link,
  // Zyxel/ZyXEL and HiLook/Hilook by Hikvision as separate vendor strings.
  const BRAND_ALIASES = {
    'hilook-by-hikvision': 'HiLook',
    'hp-poly': 'Poly',
    'ajax-systems': 'Ajax',
    'apc-by-schneider-electric': 'APC'
  };
  // Vendors that assert the product has no brand. Checked before the
  // title/tag fallback: an "Unbranded" NVR titled "Hikvision Compatible NVR"
  // is not a Hikvision product, and badging it as one is exactly the kind of
  // claim a merchant feed review reads as misrepresentation.
  const UNBRANDED_VENDORS = /^(?:unbranded|generic|no brand|noname|no name)$/i;
  // House vendors. These stay after the fallback so a CLI Secure listing whose
  // title names a real brand still badges with it.
  const HOUSE_VENDORS = /^(?:test one|my store|cli secure|clisecure)$/i;
  const FAMILY_ORDER = [
    'Cameras', 'NVRs', 'DVRs', 'Storage', 'AV and displays', 'Door entry', 'Access control', 'Alarms',
    'Firewalls', 'Licences and renewals',
    'Switches', 'Wireless access points', 'Wireless bridges', 'Routers', 'Network controllers', 'Cellular and broadband', 'NAS', 'Cables and connectors', 'Remote KVM', 'IP phones', 'Video phones',
    'Hotel phones', 'Conference systems', 'PBX solutions', 'Gateways and ATAs',
    'DECT cordless', 'Headsets', 'Convergence solutions', 'Business SIM',
    'Cables and accessories', 'Accessories', 'Other products'
  ];
  const BREADCRUMB_FACET_ORDER = [
    'brand', 'area', 'family', 'cameraForm', 'cameraResolution', 'cameraFeatures', 'cameraIrRange',
    'recorderChannels', 'recorderPoe', 'recorderBays', 'driveType', 'storageCapacity', 'nasBays',
    'cableType', 'cableLength', 'cableUse', 'cableJacket', 'cableConductor', 'cableColour',
    'avType', 'screenSize', 'displayResolution', 'bracketMotion', 'bracketScreens', 'bracketVesa', 'bracketLoad', 'alarmComponent', 'alarmConnectivity', 'doorEntryType',
    'doorEntryTech', 'firewallTier', 'concurrentSessions', 'ngfwThroughput',
    'sslInspection', 'threatProtection', 'ipsecVpn', 'switchPorts', 'switchPower',
    'switchManagement', 'wifiStandard', 'phoneSeries', 'phoneFeatures', 'sipAccounts',
    'gatewayPorts', 'headsetConnectivity', 'accessoryFor', 'availability'
  ];
  const LEGACY_PATH_ALIASES = {
    fortinet: [['brand', 'Fortinet'], ['area', 'Cyber security'], ['family', 'Firewalls']],
    grandstream: [['brand', 'Grandstream']],
    firewalls: [['area', 'Cyber security'], ['family', 'Firewalls']],
    'firewalls-for-sale': [['area', 'Cyber security'], ['family', 'Firewalls']],
    'entry-level-firewalls': [['brand', 'Fortinet'], ['area', 'Cyber security'], ['family', 'Firewalls'], ['firewallTier', 'Entry-level firewalls']],
    'mid-range-firewalls': [['brand', 'Fortinet'], ['area', 'Cyber security'], ['family', 'Firewalls'], ['firewallTier', 'Mid-range firewalls']],
    'high-end-firewalls': [['brand', 'Fortinet'], ['area', 'Cyber security'], ['family', 'Firewalls'], ['firewallTier', 'High-end firewalls']],
    cameras: [['area', 'Surveillance'], ['family', 'Cameras']],
    nvrs: [['area', 'Surveillance'], ['family', 'NVRs']],
    dvrs: [['area', 'Surveillance'], ['family', 'DVRs']],
    'access-control': [['area', 'Surveillance'], ['family', 'Access control']],
    gateways: [['area', 'Voice and telephony'], ['family', 'Gateways and ATAs']],
    headsets: [['area', 'Voice and telephony'], ['family', 'Headsets']],
    'ip-phones': [['area', 'Voice and telephony'], ['family', 'IP phones']],
    telephones: [['area', 'Voice and telephony'], ['family', 'IP phones']],
    'ip-pbx-solutions': [['area', 'Voice and telephony'], ['family', 'PBX solutions']],
    'ip-conference': [['area', 'Voice and telephony'], ['family', 'Conference systems']],
    'iot-sim-cards': [['area', 'Business connectivity'], ['family', 'Business SIM']],
    licences: [['brand', 'Fortinet'], ['area', 'Cyber security'], ['family', 'Licences and renewals']],
    renewals: [['brand', 'Fortinet'], ['area', 'Cyber security'], ['family', 'Licences and renewals']],
    routers: [['area', 'Networking'], ['family', 'Routers']],
    switches: [['area', 'Networking'], ['family', 'Switches']],
    alarms: [['area', 'Surveillance'], ['family', 'Alarms']],
    'door-entry-intercom': [['area', 'Surveillance'], ['family', 'Door entry']],
    'door-entry': [['area', 'Surveillance'], ['family', 'Door entry']],
    'cables-accessories': [['family', 'Cables and connectors'], ['family', 'Cables and accessories']],
    surveillance: [['area', 'Surveillance']],
    networking: [['area', 'Networking']],
    server: [['area', 'Networking']],
    fiber: [['area', 'Networking']],
    'wireless-access-points': [['area', 'Networking'], ['family', 'Wireless access points']],
    telecom: [['area', 'Voice and telephony']],
    voip: [['area', 'Voice and telephony']]
  };

  const STOCK_TIERS = ['in', 'low', 'back', 'out'];
  const AVAILABILITY_LABEL = { in: 'In stock', low: 'In stock', back: 'Back order', out: 'Out of stock' };
  const STOCK_BADGE = { in: 'In stock', low: 'Low stock', back: 'Back order', out: 'Out of stock' };

  const FACET_SORTERS = {
    brand: alphaSort,
    area: orderedSort(['Surveillance', 'Cyber security', 'Networking', 'Voice and telephony', 'Business connectivity', 'Accessories']),
    family: orderedSort(FAMILY_ORDER),
    availability: orderedSort(['In stock', 'Back order', 'Out of stock']),
    cameraResolution: numericLabelSort,
    recorderChannels: numericLabelSort,
    recorderPoe: numericLabelSort,
    recorderBays: numericLabelSort,
    storageCapacity: capacitySort,
    cableType: orderedSort(['CAT5e', 'CAT6', 'CAT6A', 'CAT7A', 'HDMI', 'Fibre HDMI (AOC)', 'Patch leads', 'Connectors and couplers', 'Other cables']),
    cableLength: numericLabelSort,
    cableUse: orderedSort(['Indoor', 'Outdoor']),
    cableJacket: orderedSort(['PVC', 'LSZH', 'PE']),
    cableConductor: orderedSort(['Solid bare copper', 'CCA (copper-clad aluminium)']),
    cableColour: alphaSort,
    avType: orderedSort(['Monitors', 'TV and monitor brackets', 'HDMI extenders and splitters', 'Video matrix and switching', 'Display streaming', 'AV accessories']),
    screenSize: numericLabelSort,
    displayResolution: orderedSort(['4K UHD', '1440p', '1080p Full HD']),
    bracketMotion: orderedSort(['Fixed', 'Tilt', 'Full-motion', 'Extendable', 'Ceiling', 'Projector', 'Soundbar']),
    bracketScreens: numericLabelSort,
    bracketVesa: numericLabelSort,
    bracketLoad: numericLabelSort,
    nasBays: numericLabelSort,
    driveType: orderedSort(['Surveillance drives', 'NAS drives', 'Enterprise drives', 'Desktop drives', 'SSDs', 'Memory cards', 'Other storage']),
    cameraIrRange: orderedSort(['Up to 20 m', '21-40 m', '41-60 m', 'Over 60 m']),
    alarmComponent: orderedSort(['Panels and hubs', 'Keypads', 'Motion detectors', 'Door and window contacts', 'Sirens', 'Fire and CO detectors', 'Modules and accessories']),
    alarmConnectivity: orderedSort(['Wireless', 'Wired']),
    doorEntryType: orderedSort(['Complete kits', 'Door stations and doorbells', 'Indoor monitors', 'Modular parts', 'Brackets and accessories']),
    doorEntryTech: orderedSort(['IP / PoE', '2-Wire', 'WiFi']),
    firewallTier: orderedSort(['Entry-level firewalls', 'Mid-range firewalls', 'High-end firewalls']),
    concurrentSessions: numericLabelSort,
    ngfwThroughput: rateSort,
    sslInspection: rateSort,
    threatProtection: rateSort,
    ipsecVpn: rateSort,
    switchPorts: numericLabelSort,
    accessoryFor: orderedSort([
      'Camera accessories', 'Recorder accessories', 'Switch and network accessories',
      'Wireless accessories', 'Power and PoE', 'Storage', 'Cabling',
      'Phone and headset accessories', 'Racks and mounting', 'Other accessories'
    ]),
    wifiStandard: orderedSort(['Wi-Fi 5', 'Wi-Fi 6', 'Wi-Fi 6E', 'Wi-Fi 7']),
    sipAccounts: numericLabelSort,
    gatewayPorts: numericLabelSort
  };

  // Facets that render without needing a matching block in the collection
  // template. Ordering inside the sidebar still follows BREADCRUMB_FACET_ORDER.
  const DEFAULT_FACET_BLOCKS = [
    { key: 'accessoryFor', label: 'Accessory for', open: true, search: false, visible: 10 },
    { key: 'driveType', label: 'Drive type', open: true, search: false, visible: 10 },
    { key: 'storageCapacity', label: 'Capacity', open: true, search: false, visible: 12 },
    { key: 'nasBays', label: 'Drive bays', open: true, search: false, visible: 10 },
    { key: 'cableType', label: 'Cable type', open: true, search: false, visible: 10 },
    { key: 'cableLength', label: 'Length', open: true, search: false, visible: 12 },
    { key: 'cableUse', label: 'Indoor / outdoor', open: true, search: false, visible: 4 },
    { key: 'cableJacket', label: 'Jacket', open: false, search: false, visible: 4 },
    { key: 'cableConductor', label: 'Conductor', open: false, search: false, visible: 4 },
    { key: 'cableColour', label: 'Colour', open: false, search: false, visible: 10 },
    { key: 'avType', label: 'AV product type', open: true, search: false, visible: 8 },
    { key: 'screenSize', label: 'Screen size', open: true, search: false, visible: 10 },
    { key: 'displayResolution', label: 'Resolution', open: true, search: false, visible: 4 },
    { key: 'bracketMotion', label: 'Bracket type', open: true, search: false, visible: 8 },
    { key: 'bracketScreens', label: 'Fits screens', open: true, search: false, visible: 8 },
    { key: 'bracketVesa', label: 'VESA', open: false, search: false, visible: 8 },
    { key: 'bracketLoad', label: 'Max load', open: false, search: false, visible: 6 }
  ];

  const compactCache = new Map();

  function alphaSort(a, b) {
    return a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: 'base' });
  }

  function orderedSort(order) {
    return (a, b) => {
      const aIndex = order.indexOf(a.label);
      const bIndex = order.indexOf(b.label);
      if (aIndex === -1 && bIndex === -1) return alphaSort(a, b);
      if (aIndex === -1) return 1;
      if (bIndex === -1) return -1;
      return aIndex - bIndex;
    };
  }

  function numericLabelSort(a, b) {
    return numberFromLabel(a.label) - numberFromLabel(b.label) || alphaSort(a, b);
  }

  // "512 GB" before "1 TB": capacity labels compared in gigabytes.
  function capacityGb(label) {
    const m = String(label).match(/([\d.]+)\s*(TB|GB)/i);
    return m ? Number(m[1]) * (/tb/i.test(m[2]) ? 1000 : 1) : 0;
  }

  function capacitySort(a, b) {
    return capacityGb(a.label) - capacityGb(b.label) || alphaSort(a, b);
  }

  function rateSort(a, b) {
    return rateToMbps(a.label) - rateToMbps(b.label) || alphaSort(a, b);
  }

  function numberFromLabel(value) {
    const text = String(value).replace(/,/g, '');
    const number = Number.parseFloat(text) || 0;
    if (/million|\bm\b/i.test(text)) return number * 1000000;
    return number;
  }

  function rateToMbps(value) {
    const match = String(value).match(/([\d.]+)\s*(g|m)bps/i);
    if (!match) return Number.MAX_SAFE_INTEGER;
    return Number.parseFloat(match[1]) * (match[2].toLowerCase() === 'g' ? 1000 : 1);
  }

  function handleize(value) {
    return String(value || '')
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  }

  function cleanText(value) {
    return String(value || '').replace(/\\n/g, ' ').replace(/\s+/g, ' ').trim();
  }

  function escapeHtml(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function unique(values) {
    return [...new Set(values.filter(Boolean))];
  }

  function hasRestrictedPriceBrand(product) {
    // Restriction is decided by the real vendor field and curated tags only —
    // title/type text can name another brand incidentally (e.g. a Technomate
    // camera titled "... Hikvision Compatible") and must not trigger the lock.
    // Tokens are checked for exact membership, not joined into one string and
    // substring-matched — a joined-string `.includes('technomate')` also
    // matches the unrelated staging tag "technomate-auto-draft", which
    // locked the price on every product from that import batch regardless
    // of its real vendor (Hikvision, Ajax, CORE... included).
    return RESTRICTED_PRICE_BRANDS.has(handleize(product?.vendor));
  }

  function productSourceHandle(product) {
    return [
      product?.brand,
      product?.vendor,
      product?.title,
      product?.productType,
      product?.handle,
      ...(Array.isArray(product?.tags) ? product.tags : [])
    ].map(handleize).join(' ');
  }

  function isSimQuoteProduct(product) {
    const source = productSourceHandle(product);
    return ['sim-card', 'sim-cards', 'sims', 'iot-sim', 'mobile-sim', 'business-sim', 'vodafone-sim']
      .some(token => source.includes(token));
  }

  function isFortinetQuoteProduct(product, threshold) {
    const source = productSourceHandle(product);
    const isFortinet = source.includes('fortinet') || source.includes('fortigate');
    return isFortinet && Number(product?.priceMax || 0) >= Number(threshold || 0);
  }

  // No fixed sell price exists for these SKUs (bespoke Fortinet quote items),
  // so the underlying price is a placeholder £0.00 — showing that on a card
  // reads as broken and gets the listing flagged in Google Merchant Center's
  // feed. Mirrors the price-on-request handling in price-display.liquid,
  // which only covers server-rendered cards — this JS renderer builds cards
  // independently for the dynamic catalogue, so it needs its own check.
  function isPriceOnRequest(product) {
    const tags = Array.isArray(product?.tags) ? product.tags.map(handleize) : [];
    return tags.includes('price-on-request');
  }

  function parseProductDocument(html) {
    const documentNode = new DOMParser().parseFromString(String(html || ''), 'text/html');
    return {
      documentNode,
      text: cleanText(documentNode.body?.textContent || '')
    };
  }

  function extractRows(documentNode) {
    const output = [];
    documentNode.querySelectorAll('table').forEach(table => {
      const rows = Array.from(table.querySelectorAll('tr'));
      rows.forEach((row, index) => {
        const cells = Array.from(row.querySelectorAll(':scope > th, :scope > td')).map(cell => cleanText(cell.textContent));
        if (cells.length >= 2 && cells[0]) {
          output.push({ label: cells[0], value: cleanText(cells.slice(1).join(' ')) });
        }

        const nextCells = rows[index + 1]
          ? Array.from(rows[index + 1].querySelectorAll(':scope > th, :scope > td')).map(cell => cleanText(cell.textContent))
          : [];
        const headerLike = row.querySelectorAll(':scope > th').length === cells.length;
        if (headerLike && cells.length > 1 && nextCells.length >= cells.length) {
          cells.forEach((label, cellIndex) => {
            if (label) output.push({ label, value: nextCells[cellIndex] || '' });
          });
        }
      });
    });
    return output;
  }

  function findRowValue(rows, patterns) {
    for (const pattern of patterns) {
      const row = rows.find(item => pattern.test(item.label));
      if (row?.value) return row.value;
    }
    return '';
  }

  function normalizeRate(value) {
    const match = cleanText(value).match(/(\d+(?:[.,]\d+)?)\s*(Gbps|Mbps)/i);
    if (!match) return '';
    const amount = Number.parseFloat(match[1].replace(',', '.'));
    const displayed = Number.isInteger(amount) ? String(amount) : String(amount).replace(/0+$/, '').replace(/\.$/, '');
    return `${displayed} ${match[2].toLowerCase() === 'gbps' ? 'Gbps' : 'Mbps'}`;
  }

  function normalizeSessionCount(value) {
    const text = cleanText(value).replace(/,/g, '');
    const match = text.match(/(\d+(?:\.\d+)?)\s*(million|m)?\b/i);
    if (!match) return '';
    let count = Number.parseFloat(match[1]);
    if (match[2]) count *= 1000000;
    if (!Number.isFinite(count) || count < 1000) return '';
    return Math.round(count).toLocaleString('en-GB');
  }

  function detectBrand(vendor, title, tags) {
    // The vendor field is authoritative: a product whose vendor is Technomate
    // but whose title says "... Hikvision Compatible" must badge as Technomate.
    const vendorHandle = handleize(vendor);
    if (UNBRANDED_VENDORS.test(cleanText(vendor))) return '';
    if (BRAND_ALIASES[vendorHandle]) return BRAND_ALIASES[vendorHandle];
    const vendorMatch = BRAND_NAMES.find(brand => vendorHandle === handleize(brand));
    if (vendorMatch) return vendorMatch;
    const source = handleize(`${vendor} ${title} ${tags.join(' ')}`);
    const match = BRAND_NAMES.find(brand => source.includes(handleize(brand)));
    if (match) return match;
    const normalizedVendor = cleanText(vendor);
    if (!normalizedVendor || HOUSE_VENDORS.test(normalizedVendor)) return '';
    return normalizedVendor.replace(/\b\w/g, character => character.toUpperCase());
  }

  function classifyFamily(product, brand) {
    const title = handleize(product.title);
    const type = handleize(product.product_type);
    const tags = product.tags.map(handleize);
    const source = `${title} ${type} ${tags.join(' ')}`;
    const has = (...terms) => terms.some(term => source.includes(handleize(term)));
    // Handleized text joins words with hyphens, so has('ont') also matches
    // "front" and "mount". Anything that needs a word boundary tests this.
    const rawText = `${product.title} ${product.product_type} ${tags.join(' ')}`;
    const re = (pattern) => pattern.test(rawText);

    // Cables and connectors before the accessory type rule: Technomate files
    // HDMI and CAT cable as CABLES & ACCESSORIES, which read as a spare part.
    // A box that carries a signal (extender, splitter, tester) is not a cable.
    if ((/\bcat\s?(?:5e?|6a?|7a?|8)\b|\bhdmi\b[^|]{0,60}\bcable\b|\bcable\b[^|]{0,40}\bhdmi\b|\bpatch (?:lead|cord)s?\b|\bcoax(?:ial)? cable\b|\bspeaker cable\b|\brj-?45\b[^|]{0,40}\b(?:connector|coupler|plug|boot)s?\b|\b(?:connector|coupler)s?\b[^|]{0,40}\brj-?45\b/i.test(rawText))
      && !/\b(?:extender|splitter|matrix|switch(?:es)?|balun|transceiver|sfp|tester|crimp(?:er|ing)?|tool|surge|poe\b|injector|patch panel|keystone jack)\b/i.test(rawText)) return 'Cables and connectors';

    // TV and monitor brackets, monitors and the HDMI kit between a recorder
    // and a screen. Before the accessory type rule (extenders arrive typed
    // CABLES & ACCESSORIES) and before cameras (supplier tags file monitors
    // under cameras); an "indoor monitor" is door entry and is left alone.
    if (/\btv[- ]brackets?\b/i.test(rawText) || /\b(?:tv|monitor|projector|screen)\b[^|]{0,40}\b(?:bracket|mount|arm)\b|\bsoundbar attachment\b/i.test(product.title)) return 'AV and displays';
    if ((/\bmonitor(?!ing)/i.test(product.title) && !/\b(?:indoor monitor|door|intercom|kit)\b/i.test(product.title)
      && /\d{2}\s?(?:"|”|''|-?inch|in\b)|\b(?:4k|uhd|1080p|full hd|fhd|led|lcd)\b/i.test(product.title))
      || /\bhdmi\b[^|]{0,60}\b(?:extender|splitter|matrix|switcher)\b|\b(?:extender|splitter|matrix)\b[^|]{0,60}\bhdmi\b|\bhdbaset\b|\bdisplay cast\b|\bconnect display\b|\bprotect viewport\b|\bir (?:\(infra-red\) )?extender\b|\bmatrix kit\b/i.test(rawText)) return 'AV and displays';
    // Type is authoritative for accessories: a spare stand or PSU whose
    // title names the device it fits ("Spare Desk Stand for GXP Series IP
    // Phones") must never classify into that device family from title words.
    if (/accessor|psu/.test(type)) return 'Accessories';

    // GL.iNet's naming reuses words the VoIP rules own ("security gateway",
    // "IoT gateway") and adds a category nothing else stocks (remote KVM),
    // so the brand routes before the generic rules can misfile it.
    if (brand === 'GL.iNet' || brand === 'SIMPoYo') {
      if (has('kvm', 'keyboard video mouse')) return 'Remote KVM';
      if (has('sim card', 'esim')) return 'Business SIM';
      if (has('pouch', 'case', 'mount', 'adapter', 'cable', 'antenna', 'splitter', 'replicator', 'poe module', 'power supply', 'charger')) return 'Accessories';
      return 'Routers';
    }
    // Supplier titles often say only "Recorder". Analogue/hybrid recorders are
    // DVRs and must be tested first, or "Analog HD IP Recorder" reads as an NVR.
    if (has('xvr', 'analog hd ip recorder', 'analogue recorder', 'hybrid recorder')
      || re(/\bxvr\d/i)) return 'DVRs';
    if (has('nvr', 'network video recorder')) return 'NVRs';
    if (has('dvr', 'digital video recorder')) return 'DVRs';
    if (has('recorder')) return 'NVRs';
    // Door entry and alarms must outrank the camera rule: an intercom door
    // station and an Ajax MotionCam both carry "camera" in their names.
    if (has('door entry', 'door station', 'doorbell', 'video door', 'intercom kit', 'indoor monitor',
      'sip intercom', 'mini intercom')) return 'Door entry';
    if (has('alarm system', 'alarm panel', 'alarm keypad', 'intruder', 'burglar alarm', 'wireless alarm', 'pir detector', 'ajax', 'pyronix', 'texecom', 'visonic')) return 'Alarms';
    // Dahua and Hikvision model codes name the product where the title does
    // not: IPC-HFW / IPC-HDW / DS-2CD are always cameras.
    if (has('camera', 'eyeball', 'bullet camera', 'turret camera', 'dome camera', 'ptz camera', 'fisheye')
      || re(/\bipc-h[a-z]{2}\d/i) || re(/\bds-2cd\d/i)) return 'Cameras';
    if (has('access control', 'door controller', 'video intercom', 'intercom system', 'exit button', 'card reader', 'electric release', 'maglock', 'electric lock')) return 'Access control';
    if (has('fortigate', 'firewall')) return 'Firewalls';
    if (brand === 'Fortinet' && has('licence', 'license', 'forticare', 'fortiguard', 'subscription', 'renewal')) return 'Licences and renewals';
    // "FortiAP 441K" carries none of the generic wireless-AP phrases below
    // (no "access point" substring in the model name), so every FortiAP
    // would otherwise fall through to Other products.
    if (brand === 'Fortinet' && has('fortiap')) return 'Wireless access points';
    if (brand === 'Fortinet' && has('fortiswitch')) return 'Switches';
    // FortiExtender is a cellular 4G/5G WAN failover/backup device, not a
    // router — it does not route or serve a LAN. Convergence solutions
    // (multi-connectivity WAN diversity) is the accurate family for it.
    if (brand === 'Fortinet' && has('fortiextender')) return 'Convergence solutions';
    if (has('switches', 'network switch', 'ethernet switch', 'managed switch', 'unmanaged switch')) return 'Switches';
    if (has('wireless access point', 'access points', 'wifi access point', 'wi-fi access point',
      'wifi extender', 'wi-fi extender', 'range extender', 'mesh wi-fi', 'mesh wifi', 'wi-fi system',
      'powerline', 'homeplug', 'wireless network amplifier', 'zenwifi')) return 'Wireless access points';
    if (has('router', 'wireless bridge')) return 'Routers';
    // Cellular backup, PON terminals and DSL/Ethernet extenders are all the box
    // that brings a second, or a different kind of, line into the network. Its
    // own family rather than Convergence solutions, which in this catalogue is
    // a voice concept and sits under Voice and telephony -- routing a 5G modem
    // there would file it in the phone menu. Runs after the router rule so a
    // 5G *router* stays a router.
    if (has('lte backup', 'cellular network modem', 'mobile wi-fi', 'mobile wifi', 'ethernet extender',
      'long reach ethernet', 'vdsl2 bridge', 'gpon cpe', 'lte modem')
      || re(/\b(?:4g|5g)\b.*\b(?:backup|modem|mobile|max)\b/i)
      || re(/\b(?:x?pon|ont|onu|sfu)\b/i)) return 'Cellular and broadband';
    if (has('hotel phones', 'hotel phone')) return 'Hotel phones';
    if (has('ip conference', 'conference phone', 'video conference')) return 'Conference systems';
    if (has('ip video phones', 'video phone')) return 'Video phones';
    if (has('dect cordless', 'wifi cordless', 'cordless phone')) return 'DECT cordless';
    if (has('headsets', 'headset')) return 'Headsets';
    if (has('ip pbx', 'pbx solutions', 'clouducm', 'ucm pbx', 'ucm630', 'ucm651')) return 'PBX solutions';
    if (has('gateways', 'gateway', 'telephone adapter', 'analog telephone adapters') || /\bata\b/i.test(`${product.title} ${product.product_type} ${tags.join(' ')}`)) return 'Gateways and ATAs';
    if (has('ip phones', 'voip phones', 'sip phone', 'ip phone')) return 'IP phones';
    if (has('convergence solutions', 'convergence solution')) return 'Convergence solutions';
    if (has('iot sim', 'sim card', 'sims deal')) return 'Business SIM';
    // A NAS is the box the drives go into, not a drive: its title carries
    // "HDD-SSD" or "NVMe slots" for the bays, which the drive rule below would
    // read as a drive and file it under CCTV hard drives. Drives made for a
    // NAS (IronWolf, WD Red) and bay accessories stay where they are.
    if (/\b(?:diskstation|rackstation|flashstation|unas|unifi nas)\b|\bnas\b[^|]{0,40}\b(?:enclosure|server|\d+[- ]?bay)\b|\b\d+[- ]?bay\b[^|]{0,40}\bnas\b|\bexpansion unit\b/i.test(rawText)
      && !/\b(?:ironwolf|skyhawk|barracuda|firecuda|exos|purple|wd red|red plus|red pro)\b|\bhard\s?(?:drive|disk)\b|\b(?:tray|rail kit|rail|memory module|ram|licen[cs]e|adapter|caddy)\b/i.test(rawText)) return 'NAS';
    // Storage is its own family. Surveillance and NAS drives are bought as a
    // line item beside the recorder or the NAS, and with no family of their own
    // they fell through to Other products -- which nothing in the navigation
    // links to, so 26 published drives were unreachable from the menu. This
    // sits after the recorder rules on purpose: a "4-Channel SSD NVR" is an
    // NVR, not a drive. Matching is on the raw text rather than the handleized
    // source, because the model lines that identify a drive are two words
    // ("Western Digital Purple") and a handle would have joined them.
    if (!/\badd-?on\b|\blicen[cs]e\b|\bsubscription\b/i.test(rawText)
      && (/\bhard\s?(?:drive|disk)\b|\bhdd\b|\bssd\b|\bnvme\b|\bsshd\b|\bskyhawk\b|\bironwolf\b|\bbarracuda\b|\bfirecuda\b|\bexos\b|\bmicro\s?sd\b|\bsd\s?card\b|\bmemory card\b/i.test(rawText)
        || /\b(?:western digital|wd)\s+(?:purple|red|gold|blue|black)\b/i.test(rawText))) return 'Storage';
    if (has('cables accessories', 'cables-accessories', 'security cable', 'network cable', 'coax', 'cat5', 'cat6', 'cat7', 'hdmi', 'speaker cable', 'connector', 'hard drive', 'audio video', 'balun', 'splitter')) return 'Cables and accessories';
    if (has('psu', 'accessories', 'accessory', 'sfp', 'fibre module', 'poe injector', 'mounting kit',
      'power supply', 'surveillance accessories', 'bracket', 'rain shield', 'junction box',
      'floating mount', 'wall cover', 'flush mount', 'wall mount', 'stand for', 'surge protector',
      'equipment case', 'splice box', 'poe extender', 'power adapter', 'enhancer', 'stacking module',
      'optical module', 'multi-mode module', 'single-mode module', 'expansion microphone',
      'varifocal lens', 'vari-focal lens')
      || re(/\bfan for\b|\bhot-swappable fan\b|\bspare\b/i)) return 'Accessories';
    // What used to fall through to Other products, which no menu reaches.
    if (re(/\b(?:rack ?mount|bezel|patch panel|blank\b|dust cover|stacking kit|rail kit|mount kit|radome|angle mount|keyfob|key fob|dongle|bluetooth handset|usb adapter|poe\+*|injector|weatherproof enclosure)\b/i)) return 'Accessories';
    if (re(/\b(?:bridge|nanobeam|powerbeam|litebeam|nanostation|locom?\d|airmax|airfiber|lbe-|pbe-|nbe-|udb\b|wave[- ](?:pro|pico|nano|ap|mlo)|ltu\b)/i)) return 'Wireless bridges';
    if (re(/\b(?:cloud key|uck-|hardware controller|controller)\b|\boc\d{3}\b/i)) return 'Network controllers';
    if (re(/\b(?:gate hub|access (?:retrofit )?hub|reader)\b/i)) return 'Access control';
    if (re(/\bchime\b/i)) return 'Door entry';
    if (re(/\b(?:sensor|siren|glass break|vape|flood ?light|smart lighting|lighting relay|superlink|horn speaker|sos button|panic)\b/i)) return 'Alarms';
    if (re(/\b(?:roompanel|room panel|mcore|byod|presentation|teams|zoom|public announcement)\b/i)) return 'Conference systems';
    if (re(/\bsip-t\d{2}/i)) return 'IP phones';
    if (re(/\btheta\b/i)) return 'Cameras';
    return 'Other products';
  }

  function areaForFamily(family, product, tags) {
    if (['Cameras', 'NVRs', 'DVRs', 'Access control', 'Door entry', 'Alarms'].includes(family)) return 'Surveillance';
    if (['Firewalls', 'Licences and renewals'].includes(family)) return 'Cyber security';
    if (['Switches', 'Wireless access points', 'Wireless bridges', 'Routers', 'Network controllers', 'Remote KVM', 'Cellular and broadband', 'NAS', 'Cables and connectors'].includes(family)) return 'Networking';
    if (family === 'AV and displays') return 'Surveillance';
    if (['IP phones', 'Video phones', 'Hotel phones', 'Conference systems', 'PBX solutions', 'Gateways and ATAs', 'DECT cordless', 'Headsets', 'Convergence solutions'].includes(family)) return 'Voice and telephony';
    if (family === 'Business SIM') return 'Business connectivity';
    // A surveillance drive belongs with the recorders it is bought for; a NAS
    // drive belongs with the network storage it goes into. Same family, two
    // different places in the navigation.
    if (family === 'Storage') {
      const text = `${product?.title || ''} ${product?.product_type || ''} ${(tags || []).join(' ')}`;
      return /surveillance|\bcctv\b|purple|skyhawk|security|\bvideo\b/i.test(text)
        ? 'Surveillance'
        : 'Networking';
    }
    return 'Accessories';
  }

  function rawTextOf(product, tags) {
    return `${product.title} ${product.product_type} ${(tags || []).join(' ')}`;
  }

  function buildProductRecord(product, rank) {
    const tags = Array.isArray(product.tags) ? product.tags.map(cleanText) : [];
    const brand = detectBrand(product.vendor, product.title, tags);
    const family = classifyFamily(product, brand);
    const area = areaForFamily(family, product, tags);
    const { documentNode, text: bodyText } = parseProductDocument(product.body_html);
    const rows = extractRows(documentNode);
    const source = cleanText(`${product.title} ${product.product_type} ${tags.join(' ')} ${bodyText}`);
    const lower = source.toLowerCase();
    const facets = {
      brand: brand ? [brand] : [],
      area: [area],
      family: [family],
      availability: []
    };
    const add = (key, value) => {
      if (!value) return;
      if (!facets[key]) facets[key] = [];
      facets[key].push(value);
    };

    const variants = Array.isArray(product.variants) ? product.variants : [];
    const stock = bestStock(variants.map(stockStateOf));
    // "In stock" means on the shelf. A line that only takes back orders at
    // zero stock is buyable but not in stock, and is filtered as such.
    const available = isInStock(stock);
    const orderable = stock !== 'out';
    add('availability', AVAILABILITY_LABEL[stock]);

    if (family === 'Cameras') {
      // Technology split first: the catalogue now carries TVI/analogue lines
      // alongside IP, so "IP cameras" stopped being a safe default.
      if (/\btvi\b|\bahd\b|\bcvi\b|analog(?:ue)?\b|\bsdi\b/i.test(source)) add('cameraForm', 'TVI / analogue cameras');
      else add('cameraForm', 'IP cameras');
      if (/\bsdi\b/i.test(source)) add('cameraForm', 'SDI cameras');
      if (/\beyeball\b/i.test(source)) add('cameraForm', 'Eyeball cameras');
      if (/\bbullet\b/i.test(source)) add('cameraForm', 'Bullet cameras');
      if (/\bturret\b/i.test(source)) add('cameraForm', 'Turret cameras');
      if (/\bdome\b/i.test(source)) add('cameraForm', 'Dome cameras');
      if (/\bfisheye\b|fish-eye/i.test(source)) add('cameraForm', 'Fisheye cameras');
      if (/\bptz\b|pan[ -]tilt[ -]zoom/i.test(source)) add('cameraForm', 'PTZ cameras');
      if (/\bwi-?fi\b|\bwireless camera/i.test(source)) add('cameraForm', 'Wi-Fi cameras');
      if (/colorvu|colourvu|color and audio|colour and audio/i.test(source)) add('cameraForm', 'Colour and audio');

      const resolutionMatches = source.matchAll(/\b(2|3|4|5|6|8|12)\s*(?:mp|megapixel)/gi);
      for (const match of resolutionMatches) add('cameraResolution', `${match[1]} MP`);
      if (/\b2k\b/i.test(source)) add('cameraResolution', '2K');
      if (/\b3k\b/i.test(source)) add('cameraResolution', '3K');
      if (/\b4k\b/i.test(source)) add('cameraResolution', '4K');

      if (/\bpoe\b|power over ethernet/i.test(source)) add('cameraFeatures', 'PoE');
      if (/built[- ]?in mic|microphone|two[- ]way audio|audio/i.test(source)) add('cameraFeatures', 'Audio');
      if (/acusense/i.test(source)) add('cameraFeatures', 'AcuSense');
      if (/colorvu|colourvu|full color|full colour/i.test(source)) add('cameraFeatures', 'Colour night vision');
      if (/\bai\b|deep learning|human detection|vehicle detection/i.test(source)) add('cameraFeatures', 'AI analytics');
      if (/varifocal|motorised zoom|motorized zoom/i.test(source)) add('cameraFeatures', 'Varifocal / zoom');
      if (/\bir\b|infrared/i.test(source)) add('cameraFeatures', 'Infrared');
      if (/outdoor|ip66|ip67/i.test(source)) add('cameraFeatures', 'Outdoor rated');
      if (/\banpr\b|licen[cs]e plate/i.test(source)) add('cameraFeatures', 'ANPR');
      if (/solar|battery powered|built-in battery/i.test(source)) add('cameraFeatures', 'Solar / battery');
      if (/speaker|strobe|siren|active deterrence/i.test(source)) add('cameraFeatures', 'Speaker and strobe');
      if (/auto[- ]?track/i.test(source)) add('cameraFeatures', 'Auto-tracking');
      const irMatch = source.match(/\b(\d{1,3})\s*m\s+(?:ir\b|hybrid|colou?rvu|colou?r\b)/i);
      if (irMatch) {
        const metres = Number(irMatch[1]);
        const range = metres <= 20 ? 'Up to 20 m' : metres <= 40 ? '21-40 m' : metres <= 60 ? '41-60 m' : 'Over 60 m';
        add('cameraIrRange', range);
      }
    }

    if (['NVRs', 'DVRs'].includes(family)) {
      const channelMatch = source.match(/\b(4|8|16|32|64|128)\s*(?:-|x| )?(?:channel|ch)\b/i);
      if (channelMatch) add('recorderChannels', `${channelMatch[1]} channels`);
      const poeMatch = source.match(/\b(4|8|16|24)\s*(?:-|x| )?poe\b/i);
      if (poeMatch) add('recorderPoe', `${poeMatch[1]} PoE ports`);
      if (/\bpoc\b|apoc\b/i.test(source)) add('recorderPoe', 'PoC over coax');
      const bayMatch = source.match(/\b(\d)\s*-\s*sata\b/i);
      if (bayMatch) add('recorderBays', `${bayMatch[1]} drive ${bayMatch[1] === '1' ? 'bay' : 'bays'}`);
      if (/\bessd\b|built[- ]?in\s+(?:\d+\s*tb\s+)?ssd|ssd\s+built[- ]?in/i.test(source)) add('recorderBays', 'Built-in SSD');
    }

    if (family === 'Storage') {
      // Capacity from the title: a description lists every size in the range.
      const size = String(product.title || '').match(/\b(\d+(?:\.\d+)?)\s*(TB|GB)\b/i);
      if (size) add('storageCapacity', `${Number(size[1])} ${size[2].toUpperCase()}`);
      const text = `${product.title} ${product.product_type} ${tags.join(' ')}`;
      if (/micro\s?sd|\bsd\s?card\b|memory card/i.test(text)) add('driveType', 'Memory cards');
      else if (/\bssd\b|\bnvme\b|\bm\.2\b/i.test(text) && !/\bhdd\b|hard\s?drive/i.test(text)) add('driveType', 'SSDs');
      else if (/skyhawk|purple|surveillance|\bcctv\b|video/i.test(text)) add('driveType', 'Surveillance drives');
      else if (/ironwolf|wd red|red plus|red pro|\bnas\b/i.test(text)) add('driveType', 'NAS drives');
      else if (/\bexos\b|wd gold|ultrastar|enterprise/i.test(text)) add('driveType', 'Enterprise drives');
      else if (/barracuda|wd blue|wd black|desktop/i.test(text)) add('driveType', 'Desktop drives');
      else add('driveType', 'Other storage');
    }

    if (family === 'Cables and connectors') {
      const t = String(product.title || '');
      if (/\b(?:aoc|active optical|fib(?:re|er)\b[^|]{0,20}hdmi)/i.test(t)) add('cableType', 'Fibre HDMI (AOC)');
      else if (/\bhdmi\b/i.test(t)) add('cableType', 'HDMI');
      else if (/\b(?:connector|coupler|plug|boot)s?\b/i.test(t)) add('cableType', 'Connectors and couplers');
      else if (/\bpatch (?:lead|cord)/i.test(t)) add('cableType', 'Patch leads');
      else if (/\bcat\s?7a?\b/i.test(t)) add('cableType', 'CAT7A');
      else if (/\bcat\s?6a\b/i.test(t)) add('cableType', 'CAT6A');
      else if (/\bcat\s?6\b/i.test(t)) add('cableType', 'CAT6');
      else if (/\bcat\s?5e?\b/i.test(t)) add('cableType', 'CAT5e');
      else add('cableType', 'Other cables');
      if (!/\b(?:connector|coupler|plug|boot)s?\b/i.test(t)) {
        const len = t.match(/\b(\d+(?:\.\d+)?)\s?m\b/i);
        if (len) add('cableLength', `${Number(len[1])} m`);
      }
      if (/\boutdoor\b|\bexternal\b/i.test(t)) add('cableUse', 'Outdoor');
      else if (/\bindoor\b|\binternal\b/i.test(t)) add('cableUse', 'Indoor');
      if (/\blszh\b/i.test(t)) add('cableJacket', 'LSZH');
      else if (/\bpvc\b/i.test(t)) add('cableJacket', 'PVC');
      else if (/,\s*pe\b|\bpe jacket\b/i.test(t)) add('cableJacket', 'PE');
      if (/\bbare copper\b|\bpure copper\b|\bofc\b/i.test(t)) add('cableConductor', 'Solid bare copper');
      else if (/\bcca\b|copper[- ]clad/i.test(t)) add('cableConductor', 'CCA (copper-clad aluminium)');
      const colour = t.match(/[—-]\s*(White|Black|Blue|Green|Orange|Purple|Red|Yellow|Grey|Gray)\s*$/i);
      if (colour) add('cableColour', colour[1][0].toUpperCase() + colour[1].slice(1).toLowerCase().replace('gray', 'grey'));
      // A cable sold in several colours carries them as its Colour option.
      colourVariantsOf(product).forEach(entry => add('cableColour', entry.colour));
    }

    if (family === 'AV and displays') {
      const t = String(product.title || '');
      const isBracket = /\b(?:bracket|mount|arm|soundbar attachment)\b/i.test(t) || /\btv[- ]brackets?\b/i.test(rawTextOf(product, tags));
      if (isBracket) {
        add('avType', 'TV and monitor brackets');
        if (/\bprojector\b/i.test(t)) add('bracketMotion', 'Projector');
        else if (/\bsoundbar\b/i.test(t)) add('bracketMotion', 'Soundbar');
        else {
          if (/\bceiling\b/i.test(t)) add('bracketMotion', 'Ceiling');
          if (/\bextendable\b/i.test(t)) add('bracketMotion', 'Extendable');
          if (/full[- ]motion|swivel|articulat/i.test(t)) add('bracketMotion', 'Full-motion');
          if (/\btilt/i.test(t)) add('bracketMotion', 'Tilt');
          if (/\bfixed\b|\bflat\b/i.test(t)) add('bracketMotion', 'Fixed');
        }
        const range = t.match(/(\d{2})\s?(?:"|”|''|inch)?\s*(?:to|-|–)\s*(\d{2,3})\s?(?:"|”|''|inch)/i);
        if (range) add('bracketScreens', `${range[1]}" to ${range[2]}"`);
        const vesa = t.match(/\bvesa\s*(\d{2,3})\s?x\s?(\d{2,3})/i);
        if (vesa) add('bracketVesa', `${vesa[1]}x${vesa[2]}`);
        const load = t.match(/up to\s*(\d+)\s?kg/i);
        if (load) add('bracketLoad', `Up to ${load[1]} kg`);
      } else if (/\bmonitor(?!ing)/i.test(t)) {
        add('avType', 'Monitors');
        const size = t.match(/(\d{2})(?:\.\d)?\s?(?:"|”|''|-?inch|in\b)/i);
        if (size) add('screenSize', `${size[1]}"`);
        if (/\b(?:4k|uhd|2160p|8mp)\b/i.test(t)) add('displayResolution', '4K UHD');
        else if (/\b(?:1440p|2k|qhd)\b/i.test(t)) add('displayResolution', '1440p');
        else if (/\b(?:1080p|full hd|fhd)\b/i.test(t)) add('displayResolution', '1080p Full HD');
      } else if (/\bmatrix\b/i.test(t)) add('avType', 'Video matrix and switching');
      else if (/\b(?:extender|splitter|hdbaset)\b/i.test(t)) add('avType', 'HDMI extenders and splitters');
      else if (/\b(?:display cast|connect display|viewport)\b/i.test(t)) add('avType', 'Display streaming');
      else add('avType', 'AV accessories');
    }

    if (family === 'NAS') {
      const bays = String(product.title || '').match(/\b(\d{1,2})[- ]?bay\b/i);
      if (bays) add('nasBays', `${Number(bays[1])} ${bays[1] === '1' ? 'bay' : 'bays'}`);
      const size = String(product.title || '').match(/\b(\d+(?:\.\d+)?)\s*(TB|GB)\b/i);
      if (size) add('storageCapacity', `${Number(size[1])} ${size[2].toUpperCase()}`);
    }

    if (family === 'Alarms') {
      if (/\bwireless\b|wi-?fi|jeweller/i.test(source)) add('alarmConnectivity', 'Wireless');
      if (/\bwired\b|hardwired|\bbus\b/i.test(source)) add('alarmConnectivity', 'Wired');
      if (/\bhub\b|control panel|\bpanel\b|enforcer/i.test(source)) add('alarmComponent', 'Panels and hubs');
      if (/keypad/i.test(source)) add('alarmComponent', 'Keypads');
      if (/\bpir\b|motion|curtain|motionprotect|motioncam/i.test(source)) add('alarmComponent', 'Motion detectors');
      if (/door contact|window contact|magnetic contact|doorprotect|shock|vibration|glassprotect|glass break/i.test(source)) add('alarmComponent', 'Door and window contacts');
      if (/siren|sounder|bell box|streetsiren|homesiren/i.test(source)) add('alarmComponent', 'Sirens');
      if (/smoke|heat detector|fireprotect|carbon monoxide|\bco\s+detector\b|leak|flood|leaksprotect/i.test(source)) add('alarmComponent', 'Fire and CO detectors');
      if (/relay|module|repeater|range extend|rex\b|transmitter|button\b|spacecontrol|dummy|accessory|bracket|backplate/i.test(source)) add('alarmComponent', 'Modules and accessories');
    }

    if (family === 'Door entry') {
      if (/\bkit\b/i.test(source)) add('doorEntryType', 'Complete kits');
      if (/door station|outdoor station|doorbell|door phone|\bvdp\b/i.test(source)) add('doorEntryType', 'Door stations and doorbells');
      if (/indoor monitor|indoor station|answering unit|touch screen|monitor\b/i.test(source)) add('doorEntryType', 'Indoor monitors');
      if (/module\b|modular/i.test(source)) add('doorEntryType', 'Modular parts');
      if (/bracket|rain shield|flush mount|surface mount|back box|chime\b/i.test(source)) add('doorEntryType', 'Brackets and accessories');
      if (/2[- ]wire|two[- ]wire/i.test(source)) add('doorEntryTech', '2-Wire');
      else if (/\bip\b|\bpoe\b|\bsip\b/i.test(source)) add('doorEntryTech', 'IP / PoE');
      if (/wi-?fi|wireless/i.test(source)) add('doorEntryTech', 'WiFi');
    }

    if (family === 'Firewalls') {
      const tagSource = tags.map(handleize).join(' ');
      if (tagSource.includes('entry-level-firewalls')) add('firewallTier', 'Entry-level firewalls');
      else if (tagSource.includes('mid-range-firewalls')) add('firewallTier', 'Mid-range firewalls');
      else if (tagSource.includes('high-end-firewalls')) add('firewallTier', 'High-end firewalls');

      const concurrent = normalizeSessionCount(findRowValue(rows, [/^concurrent sessions(?: \(tcp\))?/i, /^concurrent sessions$/i]));
      const ngfw = normalizeRate(findRowValue(rows, [/^ngfw throughput/i, /^ngfw$/i]));
      const ssl = normalizeRate(findRowValue(rows, [/^ssl inspection throughput/i]));
      const threat = normalizeRate(findRowValue(rows, [/^threat protection throughput/i, /^threat protection$/i]));
      const ipsec = normalizeRate(findRowValue(rows, [/^ipsec vpn throughput/i, /^vpn$/i]));
      add('concurrentSessions', concurrent);
      add('ngfwThroughput', ngfw);
      add('sslInspection', ssl);
      add('threatProtection', threat);
      add('ipsecVpn', ipsec);
    }

    if (family === 'Switches') {
      const portMatch = source.match(/\b(5|6|8|10|12|16|24|28|32|48|52)\s*(?:-|x| )?ports?\b/i);
      if (portMatch) add('switchPorts', `${portMatch[1]} ports`);
      add('switchPower', /\bpoe\+?\b|power over ethernet/i.test(source) ? 'PoE' : 'Non-PoE');
      if (/\bunmanaged\b/i.test(source)) add('switchManagement', 'Unmanaged');
      else if (/layer\s*3|\bl3\b/i.test(source)) add('switchManagement', 'Layer 3 managed');
      else if (/managed|layer\s*2|\bl2\b/i.test(source)) add('switchManagement', 'Managed');
    }

    if (['Wireless access points', 'Routers'].includes(family)) {
      if (/wi-?fi\s*7|802\.11be/i.test(source)) add('wifiStandard', 'Wi-Fi 7');
      if (/wi-?fi\s*6e/i.test(source)) add('wifiStandard', 'Wi-Fi 6E');
      else if (/wi-?fi\s*6|802\.11ax|\bax\d/i.test(source)) add('wifiStandard', 'Wi-Fi 6');
      if (/wi-?fi\s*5|802\.11ac/i.test(source)) add('wifiStandard', 'Wi-Fi 5');
    }

    if (['IP phones', 'Video phones', 'Hotel phones', 'DECT cordless'].includes(family)) {
      const seriesMatch = product.title.match(/\b(GRP|GXP|GXV|GHP|WP|DP)\d+/i);
      if (seriesMatch) add('phoneSeries', seriesMatch[1].toUpperCase());
      if (/\bpoe\b/i.test(source)) add('phoneFeatures', 'PoE');
      if (/\bwi-?fi\b|\bwireless\b/i.test(source)) add('phoneFeatures', 'Wi-Fi');
      if (/bluetooth/i.test(source)) add('phoneFeatures', 'Bluetooth');
      if (/touchscreen|touch screen/i.test(source)) add('phoneFeatures', 'Touchscreen');
      if (/video phone|video calling|video screen/i.test(source)) add('phoneFeatures', 'Video');
      if (/cordless|\bdect\b/i.test(source)) add('phoneFeatures', 'Cordless');
      const sipMatch = source.match(/\b(1|2|3|4|6|8|10|12|16)\s*(?:sip accounts?|lines?)\b/i);
      if (sipMatch) add('sipAccounts', `${sipMatch[1]} ${Number(sipMatch[1]) === 1 ? 'account / line' : 'accounts / lines'}`);
    }

    if (family === 'Gateways and ATAs') {
      const gatewayMatch = source.match(/\b(1|2|4|8|16|24|32|48)\s*(?:-| )?ports?\b/i);
      if (gatewayMatch) add('gatewayPorts', `${gatewayMatch[1]} ports`);
      if (/\bfxs\b/i.test(source)) add('gatewayPorts', 'FXS');
      if (/\bfxo\b/i.test(source)) add('gatewayPorts', 'FXO');
    }

    if (family === 'Accessories') {
      // An accessory is shelved by the thing it belongs to, not by what it is:
      // "Camera Compact Junction Box" and "Sector antenna" are both mounts and
      // both metal, but a buyer looking for one never wants the other. Match on
      // the label only (title, type, tags) — the body copy of a camera bracket
      // routinely names switches and PoE, and would drag it into the wrong bin.
      const label = cleanText(`${product.title} ${product.product_type} ${tags.join(' ')}`);
      const is = pattern => pattern.test(label);
      // Camera and recorder first: their parts are named "mount", "junction
      // box", "bracket" — the same words the generic mounting rule owns.
      // "Lens" alone is not a camera signal — Grandstream sells a replacement
      // lens for a hotel phone — so phone parts are excluded from this branch
      // rather than relying on a later rule that never gets reached.
      if (is(/camera|\bcctv\b|dome\b|bullet\b|turret\b|lens\b|\bptz\b|surveillance/i)
        && !is(/phone\b|handset|headset/i)) add('accessoryFor', 'Camera accessories');
      else if (is(/\bnvr\b|\bdvr\b|recorder/i)) add('accessoryFor', 'Recorder accessories');
      // Converters are titled "media converter", "Fiber Converter" or just
      // "Converter" depending on the brand, and PON terminals sit in the same
      // bin for a buyer — both are the box that gets fibre into a switch.
      else if (is(/\bsfp\b|sfp\+|sfp28|qsfp|transceiver|\bgbic\b|fib(?:re|er)\s+module|\bconverter\b|konverter|\bxpon\b|\bgpon\b|\bont\b|\bonu\b/i)) add('accessoryFor', 'Switch and network accessories');
      // A gain figure in dBi is an antenna even when the title never says so.
      else if (is(/antenna|radome|feed horn|dish\b|\bdbi\b/i)) add('accessoryFor', 'Wireless accessories');
      // PoE adapters are branded PoE+, PoE++ and PoE+++, so the plus signs
      // cannot be part of the literal match.
      else if (is(/injector|splitter|midspan|power supply|\bpsu\b|power adapter|\bpoe\+*\s*adapter|power over ethernet adapter|\bups\b|battery|charger/i)) add('accessoryFor', 'Power and PoE');
      else if (is(/hard drive|\bhdd\b|\bssd\b|micro\s?sd|memory card|\bnas\b/i)) add('accessoryFor', 'Storage');
      else if (is(/\bcable\b|patch lead|patch cord|connector|coupler|pigtail/i)) add('accessoryFor', 'Cabling');
      else if (is(/headset|handset|phone\b|\bdect\b|\bsip\b|\bpstn\b|conferenc|room kit|speakerphone/i)) add('accessoryFor', 'Phone and headset accessories');
      else if (is(/rack|patch panel|cabinet|mount|bracket|enclosure|pole\b|\bfan\b|module\b|spare/i)) add('accessoryFor', 'Racks and mounting');
      else add('accessoryFor', 'Other accessories');
    }

    if (family === 'Headsets') {
      if (/\busb\b/i.test(source)) add('headsetConnectivity', 'USB');
      if (/bluetooth/i.test(source)) add('headsetConnectivity', 'Bluetooth');
      if (/\bdect\b/i.test(source)) add('headsetConnectivity', 'DECT');
      if (/\bwireless\b/i.test(source)) add('headsetConnectivity', 'Wireless');
      if (/3\.5\s*mm|3\.5mm/i.test(source)) add('headsetConnectivity', '3.5 mm');
      if (/quick disconnect|\bqd\b/i.test(source)) add('headsetConnectivity', 'Quick disconnect');
    }

    Object.keys(facets).forEach(key => {
      facets[key] = unique(facets[key]);
    });

    const prices = variants.map(variant => Math.round(Number.parseFloat(variant.price || 0) * 100)).filter(Number.isFinite);
    const comparePrices = variants.map(variant => Math.round(Number.parseFloat(variant.compare_at_price || 0) * 100)).filter(value => value > 0);
    const selectedVariant = variants.find(variant => isInStock(stockStateOf(variant)))
      || variants.find(variant => variant.available) || variants[0] || {};
    const image = product.images?.[0]?.src || selectedVariant.featured_image?.src || '';
    const sourceVendor = cleanText(product.vendor || '');
    const vendor = brand || (!sourceVendor || /^(?:cli secure|my store|test one)$/i.test(sourceVendor) ? 'CLI Secure' : sourceVendor);

    return {
      id: product.id,
      rank,
      handle: product.handle,
      title: cleanText(product.title),
      vendor,
      brand,
      productType: cleanText(product.product_type),
      tags,
      facets,
      available,
      orderable,
      stock,
      priceMin: prices.length ? Math.min(...prices) : 0,
      priceMax: prices.length ? Math.max(...prices) : 0,
      compareAtPrice: comparePrices.length ? Math.max(...comparePrices) : 0,
      variantId: selectedVariant.id || '',
      sku: cleanText(selectedVariant.sku || ''),
      colours: colourVariantsOf(product),
      image,
      createdAt: Date.parse(product.created_at || product.published_at || 0) || 0
    };
  }

  // A variant's stock in the product page's four tiers: in, low, back, out.
  // The prebuilt index carries it (scripts/build-catalogue-index.mjs) and the
  // stock view of a product (templates/product.stock.liquid) says it live.
  // The storefront's own "available" is true at zero stock whenever back
  // orders are allowed, so on its own it can only tell orderable from not.
  function stockStateOf(variant) {
    const state = variant?.stock_state || variant?.state;
    if (STOCK_TIERS.includes(state)) return state;
    return variant?.available ? 'in' : 'out';
  }

  function bestStock(states) {
    for (const tier of STOCK_TIERS) if (states.includes(tier)) return tier;
    return 'out';
  }

  const isInStock = stock => stock === 'in' || stock === 'low';

  // The colours of a product sold in more than one, one entry per variant:
  // its option value, id, stock and photo. Empty for everything else.
  function colourVariantsOf(product) {
    const options = Array.isArray(product?.options) ? product.options : [];
    const index = options.findIndex(option => /^colou?r$/i.test(String(option?.name || '')));
    if (index < 0) return [];
    const variants = Array.isArray(product.variants) ? product.variants : [];
    if (variants.length < 2) return [];
    const images = Array.isArray(product.images) ? product.images : [];
    return variants.map(variant => {
      const raw = String(variant[`option${index + 1}`] || '').trim();
      if (!raw) return null;
      const colour = raw[0].toUpperCase() + raw.slice(1).toLowerCase().replace('gray', 'grey');
      const image = variant.featured_image?.src
        || images.find(img => Array.isArray(img.variant_ids) && img.variant_ids.includes(variant.id))?.src
        || '';
      const stock = stockStateOf(variant);
      return { colour, id: variant.id, available: isInStock(stock), orderable: stock !== 'out', stock, sku: cleanText(variant.sku || ''), image };
    }).filter(Boolean);
  }

  async function fetchProductPage(url, page) {
    const endpoint = new URL(url, window.location.origin);
    endpoint.searchParams.set('limit', '250');
    endpoint.searchParams.set('page', String(page));
    const response = await fetch(endpoint.toString(), { credentials: 'same-origin' });
    if (!response.ok) throw new Error(`Catalogue request failed (${response.status})`);
    const payload = await response.json();
    return Array.isArray(payload.products) ? payload.products : [];
  }

  async function fetchPrebuiltIndex(indexUrl, progress) {
    // A nightly job publishes the whole catalogue as one theme asset, so the
    // filters open with a single cheap CDN request instead of paging the
    // storefront API. Any failure falls back to paging — the asset is an
    // optimisation, never a dependency.
    if (!indexUrl) return null;
    try {
      const response = await fetch(indexUrl, { credentials: 'omit' });
      if (!response.ok) return null;
      const payload = await response.json();
      if (!Array.isArray(payload.products) || !payload.products.length) return null;
      // A theme copy carries whatever asset it was duplicated with, and a
      // theme published from a copy keeps serving it until the next index
      // build. Products created since then would silently vanish from the
      // filters, so an asset older than two builds loses to live paging.
      const age = Date.now() - Date.parse(payload.generatedAt || 0);
      if (!Number.isFinite(age) || age > 48 * 60 * 60 * 1000) return null;
      progress?.(`Loaded ${payload.products.length} products from the prebuilt index`);
      return payload.products;
    } catch (_) {
      return null;
    }
  }

  async function loadProductIndex(url, progress, indexUrl) {
    const memoryKey = `${INDEX_VERSION}:${url}`;
    if (compactCache.has(memoryKey)) return compactCache.get(memoryKey);

    const storageKey = `cli-dynamic-catalogue:${memoryKey}`;
    try {
      const cached = JSON.parse(sessionStorage.getItem(storageKey) || 'null');
      if (cached?.createdAt > Date.now() - CACHE_TTL && Array.isArray(cached.products)) {
        compactCache.set(memoryKey, cached.products);
        progress?.(`Loaded ${cached.products.length} cached products`);
        return cached.products;
      }
    } catch (_) {
      // Storage is optional; continue with a fresh index.
    }

    // The prebuilt asset holds the WHOLE catalogue, so it only substitutes on
    // whole-catalogue routes — a real collection page needs its scoped list.
    const wholeCatalogue = /\/collections\/all\/products\.json$/.test(new URL(url, window.location.origin).pathname);
    let rawProducts = wholeCatalogue ? await fetchPrebuiltIndex(indexUrl, progress) : null;

    if (!rawProducts) {
      const initialPages = await Promise.all([1, 2, 3, 4].map(page => fetchProductPage(url, page)));
      rawProducts = initialPages.flat();
      progress?.(`Reading ${rawProducts.length} products`);

      let nextPage = 5;
      let lastPage = initialPages[initialPages.length - 1];
      while (lastPage.length === 250) {
        lastPage = await fetchProductPage(url, nextPage++);
        rawProducts.push(...lastPage);
      }
    }

    const seen = new Set();
    rawProducts = rawProducts.filter(product => {
      if (!product?.id || seen.has(product.id)) return false;
      seen.add(product.id);
      return true;
    });

    progress?.(`Mapping specifications for ${rawProducts.length} products`);
    const products = rawProducts.map((product, index) => buildProductRecord(product, index));
    compactCache.set(memoryKey, products);

    try {
      sessionStorage.setItem(storageKey, JSON.stringify({ createdAt: Date.now(), products }));
    } catch (_) {
      // Browsers with small storage quotas still retain the in-memory index.
    }
    return products;
  }

  class DynamicCatalogue {
    constructor(root) {
      this.root = root;
      this.sidebar = root.querySelector('[data-facet-sidebar]');
      this.grid = root.querySelector('[data-collection-product-grid]');
      this.pagination = root.querySelector('[data-collection-pagination]');
      this.loading = root.querySelector('[data-filter-loading]');
      this.toolbarFilters = root.querySelector('[data-toolbar-filters]');
      this.drawerTrigger = root.querySelector('[data-facet-open]');
      this.pageSize = Number(root.dataset.productsPerPage) || 24;
      this.defaultVisible = Number(root.dataset.visibleFilterValues) || 8;
      this.products = [];
      this.state = { filters: new Map(), minPrice: null, maxPrice: null, sort: root.dataset.initialSort || 'featured', page: 1, query: '' };
      this.config = this.readConfig();
      this.ensureFallbackGroups();
      const collectionRoute = window.location.pathname.match(/\/collections\/([^/]+)/);
      this.collectionHandle = collectionRoute ? decodeURIComponent(collectionRoute[1]) : '';
      this.isScopedCollection = Boolean(this.collectionHandle) && this.collectionHandle !== 'all';
      this.impliedFamilies = new Set();
      this.boundPopstate = () => this.handlePopstate();
      this.bindEvents();
      this.initialize();
    }

    // The sidebar's groups are drawn by the theme from the editor's blocks, so
    // a facet listed in DEFAULT_FACET_BLOCKS had a config entry but nowhere
    // to render. Draw the missing ones here, in the theme's own markup.
    ensureFallbackGroups() {
      const container = this.root.querySelector('[data-facet-groups]');
      if (!container) return;
      container.querySelector('.native-facets__empty')?.remove();
      for (const block of DEFAULT_FACET_BLOCKS) {
        if (container.querySelector(`[data-custom-facet-group][data-facet-key="${block.key}"]`)) continue;
        const group = document.createElement('details');
        group.className = 'native-facet-group custom-facet-group';
        group.dataset.customFacetGroup = '';
        group.dataset.facetKey = block.key;
        group.dataset.visibleValues = String(block.visible || this.defaultVisible);
        group.dataset.enableSearch = String(Boolean(block.search));
        if (block.open) group.open = true;
        group.hidden = true;
        group.innerHTML = `
          <summary class="native-facet-group__summary">
            <span>${escapeHtml(block.label)}</span>
            <span class="native-facet-group__summary-meta">
              <span class="native-facet-group__reopen" data-facet-reopen-label hidden>See more</span>
              <span class="native-facet-group__active-count" data-group-active-count hidden></span>
              <span class="native-facet-group__chevron" aria-hidden="true"><svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"><path d="m6 9 6 6 6-6"/></svg></span>
            </span>
          </summary>
          <div class="native-facet-group__content" data-facet-content></div>`;
        container.appendChild(group);
      }
    }

    readConfig() {
      let config;
      try {
        config = JSON.parse(this.root.querySelector('[data-custom-facet-config]')?.textContent || '{"blocks":[]}');
      } catch (_) {
        config = { blocks: [] };
      }
      config.blocks = Array.isArray(config.blocks) ? config.blocks : [];
      // A facet only reaches the page if someone also added a block for it in
      // the theme editor, which makes shipping a new one a two-part change that
      // silently half-lands. Anything listed here renders on its own; a block
      // added later replaces it, so the editor still wins.
      for (const fallback of DEFAULT_FACET_BLOCKS) {
        if (!config.blocks.some(block => block?.key === fallback.key)) config.blocks.push({ ...fallback });
      }
      return config;
    }

    bindEvents() {
      this.root.addEventListener('click', event => this.handleClick(event));
      this.root.addEventListener('change', event => this.handleChange(event));
      this.root.addEventListener('input', event => this.handleInput(event));
      this.root.addEventListener('submit', event => this.handleSubmit(event));
      this.sidebar?.addEventListener('wheel', event => this.handleSidebarWheel(event), { passive: false });
      document.addEventListener('keydown', event => {
        if (event.key === 'Escape') this.closeDrawer();
      });
      window.addEventListener('popstate', this.boundPopstate);
    }

    async initialize() {
      this.root.classList.add('is-filter-loading');
      this.syncSortControls();
      try {
        this.products = await loadProductIndex(this.root.dataset.productsJsonUrl, message => this.setLoadingMessage(message), this.root.dataset.catalogueIndexUrl);
        this.impliedFamilies = this.computeImpliedFamilies();
        this.state = this.readStateFromUrl();
        this.pruneDependentFilters();
        const routeTags = this.routeTags(new URL(window.location.href));
        this.selectSoleBrandForRoute(routeTags);
        const initialGuideFrom = this.initialGuideFacet(routeTags);
        this.render({
          updateHistory: false,
          advanceFrom: initialGuideFrom,
          visibleBefore: initialGuideFrom ? this.visibleFacetKeys() : []
        });
        this.openLeadingGroups(4);
        this.root.classList.add('is-custom-catalogue-ready');
      } catch (error) {
        this.showLoadError(error);
        console.error(error);
      } finally {
        this.setLoading(false);
      }
    }

    setLoadingMessage(message) {
      const note = this.root.querySelector('[data-loading-note]');
      if (note) note.textContent = message;
    }

    setLoading(loading) {
      this.root.classList.toggle('is-filter-loading', loading);
      this.root.querySelector('[data-product-stage]')?.setAttribute('aria-busy', String(loading));
      this.loading?.setAttribute('aria-hidden', String(!loading));
    }

    showLoadError(error) {
      const title = this.root.querySelector('[data-loading-title]');
      const note = this.root.querySelector('[data-loading-note]');
      if (title) title.textContent = 'Filters could not load';
      if (note) note.textContent = 'The server-rendered products remain available. Refresh to try again.';
      this.root.querySelector('[data-results-toolbar]')?.classList.add('has-filter-error');
      if (error) this.root.dataset.filterError = error.message;
    }

    readStateFromUrl() {
      const url = new URL(window.location.href);
      const filters = new Map();
      // Neither 'area' nor 'family' has a sidebar group — the Business area
      // filter was taken out at the owner's request, and product categories
      // are chosen from the menu rather than the sidebar. Both still scope
      // category routes, the mega menu and old deep links, so their URL
      // parameters stay readable. Leaving 'family' out of this list is what
      // made every "family::" menu link open the whole catalogue: the
      // parameter was in the address bar and nothing ever read it.
      const knownKeys = unique([...this.config.blocks.map(block => block.key), 'area', 'family'])
        .filter(key => key !== 'price');

      knownKeys.forEach(key => {
        const values = unique(url.searchParams.getAll(`f_${key}`).map(cleanText));
        if (values.length) filters.set(key, new Set(values));
      });

      this.routeTags(url).forEach(tag => this.applyLegacyTag(filters, tag));

      // A deep link can name a dependent facet without the family that gates
      // it — the menu's "IP Cameras" passes only f_cameraForm. Without the
      // family, pruneDependentFilters would drop the facet as irrelevant and
      // the link would show the whole catalogue, so the gate is implied here,
      // just as the legacy tag routes imply it. An explicit family in the URL
      // always wins.
      if (!filters.get('family')?.size) {
        const GATE_FAMILIES = {
          cameraForm: ['Cameras'], cameraResolution: ['Cameras'], cameraFeatures: ['Cameras'], cameraIrRange: ['Cameras'],
          recorderChannels: ['NVRs', 'DVRs'], recorderPoe: ['NVRs', 'DVRs'], recorderBays: ['NVRs', 'DVRs'],
          driveType: ['Storage'], storageCapacity: ['Storage'], nasBays: ['NAS'],
          cableType: ['Cables and connectors'], cableLength: ['Cables and connectors'], cableUse: ['Cables and connectors'],
          cableJacket: ['Cables and connectors'], cableConductor: ['Cables and connectors'], cableColour: ['Cables and connectors'],
          avType: ['AV and displays'], screenSize: ['AV and displays'], displayResolution: ['AV and displays'],
          bracketMotion: ['AV and displays'], bracketScreens: ['AV and displays'], bracketVesa: ['AV and displays'], bracketLoad: ['AV and displays'],
          alarmComponent: ['Alarms'], alarmConnectivity: ['Alarms'],
          doorEntryType: ['Door entry'], doorEntryTech: ['Door entry'],
          firewallTier: ['Firewalls'], concurrentSessions: ['Firewalls'], ngfwThroughput: ['Firewalls'],
          sslInspection: ['Firewalls'], threatProtection: ['Firewalls'], ipsecVpn: ['Firewalls'],
          switchPorts: ['Switches'], switchPower: ['Switches'], switchManagement: ['Switches'],
          wifiStandard: ['Wireless access points', 'Routers'],
          phoneSeries: ['IP phones', 'Video phones', 'Hotel phones', 'DECT cordless'],
          phoneFeatures: ['IP phones', 'Video phones', 'Hotel phones', 'DECT cordless'],
          sipAccounts: ['IP phones', 'Video phones', 'Hotel phones', 'DECT cordless'],
          gatewayPorts: ['Gateways and ATAs'], headsetConnectivity: ['Headsets'],
          accessoryFor: ['Accessories']
        };
        for (const [facetKey, gateFamilies] of Object.entries(GATE_FAMILIES)) {
          if (!filters.get(facetKey)?.size) continue;
          gateFamilies.forEach(family => this.addFilterValue(filters, 'family', family));
          unique(gateFamilies.map(areaForFamily)).forEach(area => this.addFilterValue(filters, 'area', area));
        }
      }

      const min = Number.parseFloat(url.searchParams.get('price_min'));
      const max = Number.parseFloat(url.searchParams.get('price_max'));
      const page = Number.parseInt(url.searchParams.get('page'), 10);
      const sort = url.searchParams.get('sort_by') || this.root.dataset.initialSort || 'featured';

      // query must be present: this object replaces the initial state
      // wholesale, and an undefined query would be written into the search
      // input verbatim — the box would literally show "undefined".
      return {
        filters,
        minPrice: Number.isFinite(min) ? min : null,
        maxPrice: Number.isFinite(max) ? max : null,
        sort,
        page: Number.isFinite(page) && page > 0 ? page : 1,
        query: ''
      };
    }

    pathTags(url) {
      const basePath = new URL(this.root.dataset.collectionUrl, window.location.origin).pathname.replace(/\/$/, '');
      const currentPath = url.pathname.replace(/\/$/, '');
      if (!currentPath.startsWith(`${basePath}/`)) return [];
      return decodeURIComponent(currentPath.slice(basePath.length + 1)).split(',').map(cleanText).filter(Boolean);
    }

    initialTags() {
      return String(this.root.dataset.initialTags || '').split('|').map(cleanText).filter(Boolean);
    }

    routeTags(url) {
      return unique([...this.pathTags(url), ...this.initialTags()]);
    }

    selectSoleBrandForRoute(routeTags) {
      if (!routeTags.length || this.state.filters.get('brand')?.size) return;
      const brands = this.optionCounts('brand').filter(option => option.count > 0);
      if (brands.length !== 1) return;
      this.addFilterValue(this.state.filters, 'brand', brands[0].label);
      this.pruneDependentFilters();
    }

    initialGuideFacet(routeTags) {
      if (!routeTags.length) return null;
      if (this.state.filters.get('family')?.size) return 'family';
      if (this.state.filters.get('area')?.size) return 'area';
      if (this.state.filters.get('brand')?.size) return 'brand';
      return null;
    }

    applyLegacyTag(filters, tag) {
      const tagHandle = handleize(tag);
      const alias = LEGACY_PATH_ALIASES[tagHandle];
      if (alias) {
        alias.forEach(([key, value]) => this.addFilterValue(filters, key, value));
        return;
      }

      const priorities = ['brand', 'area', 'family', 'firewallTier', 'cameraForm', 'cameraResolution', 'recorderChannels'];
      for (const key of priorities) {
        const value = this.allFacetValues(key).find(candidate => handleize(candidate) === tagHandle);
        if (value) {
          this.addFilterValue(filters, key, value);
          if (key === 'family') this.addFilterValue(filters, 'area', areaForFamily(value));
          if (key === 'firewallTier') {
            this.addFilterValue(filters, 'brand', 'Fortinet');
            this.addFilterValue(filters, 'area', 'Cyber security');
            this.addFilterValue(filters, 'family', 'Firewalls');
          }
          if (key === 'cameraForm') {
            this.addFilterValue(filters, 'area', 'Surveillance');
            this.addFilterValue(filters, 'family', 'Cameras');
          }
          return;
        }
      }

      const legacySpecs = [
        ['spec-concurrent-', 'concurrentSessions'],
        ['spec-ngfw-', 'ngfwThroughput'],
        ['spec-ssl-', 'sslInspection'],
        ['spec-threat-', 'threatProtection'],
        ['spec-ipsec-', 'ipsecVpn']
      ];
      const mapping = legacySpecs.find(([prefix]) => tagHandle.startsWith(prefix));
      if (!mapping) return;
      const [, key] = mapping;
      const suffix = tagHandle.slice(mapping[0].length);
      const value = this.allFacetValues(key).find(candidate => handleize(candidate) === suffix || handleize(candidate).endsWith(suffix));
      if (value) filters.set(key, new Set([value]));
    }

    addFilterValue(filters, key, value) {
      if (!key || !value) return;
      if (!filters.has(key)) filters.set(key, new Set());
      filters.get(key).add(value);
    }

    allFacetValues(key) {
      return unique(this.products.flatMap(product => product.facets[key] || []));
    }

    matches(product, ignoreKey = null) {
      // The in-results search box: an installer types a model code and the
      // grid narrows instantly, counts included.
      if (this.state.query) {
        const haystack = `${product.title} ${product.sku}`.toLowerCase();
        const terms = this.state.query.toLowerCase().split(/\s+/).filter(Boolean);
        if (!terms.every(term => haystack.includes(term))) return false;
      }
      for (const [key, selected] of this.state.filters.entries()) {
        if (key === ignoreKey || selected.size === 0) continue;
        const values = product.facets[key] || [];
        if (![...selected].some(value => values.includes(value))) return false;
      }
      if (this.state.minPrice !== null && product.priceMax < this.state.minPrice * 100) return false;
      if (this.state.maxPrice !== null && product.priceMin > this.state.maxPrice * 100) return false;
      return true;
    }

    matchingProducts(ignoreKey = null) {
      return this.products.filter(product => this.matches(product, ignoreKey));
    }

    optionCounts(key) {
      const counts = new Map();
      this.matchingProducts(key).forEach(product => {
        (product.facets[key] || []).forEach(value => counts.set(value, (counts.get(value) || 0) + 1));
      });
      const selected = this.state.filters.get(key) || new Set();
      selected.forEach(value => {
        if (!counts.has(value)) counts.set(value, 0);
      });
      const options = [...counts.entries()].map(([label, count]) => ({ label, count, active: selected.has(label) }));
      return options.sort(FACET_SORTERS[key] || alphaSort);
    }

    // On a scoped collection page the visitor never picks a "Product
    // categories" value, so the family-gated facets would stay hidden.
    // Families that dominate the collection's own catalogue stand in for
    // an explicit selection instead.
    computeImpliedFamilies() {
      if (!this.isScopedCollection) return new Set();
      const counts = new Map();
      this.products.forEach(product => {
        (product.facets.family || []).forEach(value => counts.set(value, (counts.get(value) || 0) + 1));
      });
      const implied = new Set();
      const threshold = Math.max(1, this.products.length * 0.1);
      let top = null;
      counts.forEach((count, value) => {
        if (top === null || count > counts.get(top)) top = value;
        if (count >= threshold) implied.add(value);
      });
      if (top !== null) implied.add(top);
      return implied;
    }

    isFacetRelevant(key) {
      if (GLOBAL_FACETS.has(key)) return true;
      const selected = facetKey => this.state.filters.get(facetKey) || new Set();
      const family = selected('family');
      const brand = selected('brand');
      const implied = this.impliedFamilies || new Set();
      const hasFamily = values => values.some(value => family.has(value) || implied.has(value));

      if (key === 'cameraForm' || key === 'cameraFeatures' || key === 'cameraIrRange') return hasFamily(['Cameras']);
      if (key === 'cameraResolution') return hasFamily(['Cameras']);
      if (['recorderChannels', 'recorderPoe', 'recorderBays'].includes(key)) return hasFamily(['NVRs', 'DVRs']);
      if (key === 'driveType') return hasFamily(['Storage']);
      if (key === 'storageCapacity') return hasFamily(['Storage', 'NAS']);
      if (key === 'nasBays') return hasFamily(['NAS']);
      if (['cableType', 'cableLength', 'cableUse', 'cableJacket', 'cableConductor', 'cableColour'].includes(key)) return hasFamily(['Cables and connectors']);
      if (['avType', 'screenSize', 'displayResolution', 'bracketMotion', 'bracketScreens', 'bracketVesa', 'bracketLoad'].includes(key)) return hasFamily(['AV and displays']);
      if (key === 'alarmComponent' || key === 'alarmConnectivity') return hasFamily(['Alarms']);
      if (key === 'doorEntryType' || key === 'doorEntryTech') return hasFamily(['Door entry']);
      if (key === 'firewallTier') return hasFamily(['Firewalls']) || brand.has('Fortinet');
      if (['concurrentSessions', 'ngfwThroughput', 'sslInspection', 'threatProtection', 'ipsecVpn'].includes(key)) {
        return hasFamily(['Firewalls']) || brand.has('Fortinet');
      }
      if (['switchPorts', 'switchPower', 'switchManagement'].includes(key)) return hasFamily(['Switches']);
      if (key === 'wifiStandard') return hasFamily(['Wireless access points', 'Routers']);
      if (['phoneSeries', 'phoneFeatures', 'sipAccounts'].includes(key)) {
        return hasFamily(['IP phones', 'Video phones', 'Hotel phones', 'DECT cordless']);
      }
      if (key === 'gatewayPorts') return hasFamily(['Gateways and ATAs']);
      if (key === 'headsetConnectivity') return hasFamily(['Headsets']);
      if (key === 'accessoryFor') return hasFamily(['Accessories']);
      return true;
    }

    pruneDependentFilters() {
      let changed = true;
      while (changed) {
        changed = false;
        for (const key of [...this.state.filters.keys()]) {
          if (!GLOBAL_FACETS.has(key) && !this.isFacetRelevant(key)) {
            this.state.filters.delete(key);
            changed = true;
          }
        }
      }
    }

    handleClick(event) {
      const toolbarSummary = event.target.closest('.native-toolbar-filter__summary');
      if (toolbarSummary) {
        const current = toolbarSummary.closest('[data-toolbar-filter]');
        requestAnimationFrame(() => {
          this.root.querySelectorAll('[data-toolbar-filter]').forEach(filter => {
            if (filter !== current) filter.open = false;
          });
        });
        return;
      }
      const open = event.target.closest('[data-facet-open]');
      if (open) {
        event.preventDefault();
        this.openDrawer(open);
        return;
      }
      if (event.target.closest('[data-facet-close]')) {
        event.preventDefault();
        this.closeDrawer();
        return;
      }
      const clear = event.target.closest('[data-clear-filters]');
      if (clear) {
        event.preventDefault();
        this.clearFilters();
        return;
      }
      const stockToggle = event.target.closest('[data-instock-toggle]');
      if (stockToggle) {
        event.preventDefault();
        const selected = this.state.filters.get('availability') || new Set();
        if (selected.has('In stock')) selected.delete('In stock');
        else selected.add('In stock');
        if (selected.size) this.state.filters.set('availability', selected);
        else this.state.filters.delete('availability');
        this.state.page = 1;
        this.render({ updateHistory: true });
        return;
      }
      const remove = event.target.closest('[data-remove-filter-key]');
      if (remove) {
        event.preventDefault();
        this.removeFilter(remove.dataset.removeFilterKey, remove.dataset.removeFilterValue);
        return;
      }
      const more = event.target.closest('[data-facet-more]');
      if (more) {
        event.preventDefault();
        const group = more.closest('[data-custom-facet-group]');
        const expanded = more.getAttribute('aria-expanded') === 'true';
        group?.classList.toggle('is-showing-all', !expanded);
        more.setAttribute('aria-expanded', String(!expanded));
        more.querySelector('[data-facet-more-label]').textContent = expanded ? 'Show more' : 'Show less';
        return;
      }
      const pageButton = event.target.closest('[data-custom-page]');
      if (pageButton) {
        event.preventDefault();
        this.state.page = Number(pageButton.dataset.customPage) || 1;
        this.render({ updateHistory: true, scrollToResults: true });
      }
    }

    handleChange(event) {
      if (event.target.matches('[data-custom-filter-input]')) {
        const facetAnchor = this.captureFacetAnchor(event.target);
        const visibleBefore = this.visibleFacetKeys();
        const key = event.target.dataset.filterKey;
        const value = event.target.value;
        const selected = this.state.filters.get(key) || new Set();
        if (event.target.checked) selected.add(value);
        else selected.delete(value);
        if (selected.size) this.state.filters.set(key, selected);
        else this.state.filters.delete(key);
        this.state.page = 1;
        this.pruneDependentFilters();
        this.render({
          updateHistory: true,
          facetAnchor,
          advanceFrom: event.target.checked ? key : null,
          visibleBefore
        });
        return;
      }
      if (event.target.matches('[data-custom-sort]')) {
        this.state.sort = event.target.value;
        this.state.page = 1;
        this.render({ updateHistory: true });
      }
    }

    handleInput(event) {
      if (event.target.matches('[data-catalogue-search]')) {
        this.state.query = event.target.value.trim();
        this.state.page = 1;
        // The input lives in static toolbar markup, so re-rendering the grid
        // and facet counts never rebuilds it or steals its focus.
        this.render({ updateHistory: false });
        return;
      }
      if (event.target.matches('[data-facet-search]')) {
        const group = event.target.closest('[data-custom-facet-group]');
        const query = event.target.value.trim().toLowerCase();
        let visible = 0;
        group?.querySelectorAll('[data-facet-value]').forEach(item => {
          const matches = !query || item.dataset.facetLabel.includes(query);
          item.hidden = !matches;
          if (matches) visible += 1;
        });
        const empty = group?.querySelector('[data-facet-search-empty]');
        if (empty) empty.hidden = visible > 0;
        return;
      }

      const priceForm = event.target.closest('[data-custom-price-form]');
      if (!priceForm) return;
      if (event.target.matches('[data-price-range]')) {
        const minRange = priceForm.querySelector('[data-price-range="min"]');
        const maxRange = priceForm.querySelector('[data-price-range="max"]');
        let min = Number(minRange.value);
        let max = Number(maxRange.value);
        if (event.target === minRange && min > max) min = max;
        if (event.target === maxRange && max < min) max = min;
        minRange.value = String(min);
        maxRange.value = String(max);
        priceForm.elements.price_min.value = String(min);
        priceForm.elements.price_max.value = String(max);
        this.updatePriceSlider(priceForm);
        return;
      }
      if (event.target.matches('[data-price-number]')) {
        const range = priceForm.querySelector(`[data-price-range="${event.target.dataset.priceNumber}"]`);
        const fallback = event.target.dataset.priceNumber === 'min' ? 0 : Number(range.max);
        const value = Math.min(Number(range.max), Math.max(0, Number(event.target.value || fallback)));
        range.value = String(value);
        this.updatePriceSlider(priceForm);
      }
    }

    handleSubmit(event) {
      const priceForm = event.target.closest('[data-custom-price-form]');
      if (!priceForm) return;
      event.preventDefault();
      let min = Number.parseFloat(priceForm.elements.price_min.value);
      let max = Number.parseFloat(priceForm.elements.price_max.value);
      if (Number.isFinite(min) && Number.isFinite(max) && min > max) [min, max] = [max, min];
      const visibleBefore = this.visibleFacetKeys();
      this.state.minPrice = Number.isFinite(min) ? Math.max(0, min) : null;
      this.state.maxPrice = Number.isFinite(max) ? Math.max(0, max) : null;
      this.state.page = 1;
      this.render({ updateHistory: true, advanceFrom: 'price', visibleBefore });
    }

    handleSidebarWheel(event) {
      const mobile = window.matchMedia('(max-width: 989px)').matches;
      const scrollContainer = mobile ? this.root.querySelector('[data-facet-groups]') : this.sidebar;
      if (!scrollContainer || scrollContainer.scrollHeight <= scrollContainer.clientHeight) return;
      event.preventDefault();
      const maxScroll = scrollContainer.scrollHeight - scrollContainer.clientHeight;
      scrollContainer.scrollTop = Math.min(maxScroll, Math.max(0, scrollContainer.scrollTop + event.deltaY));
    }

    handlePopstate() {
      if (!this.products.length) return;
      this.state = this.readStateFromUrl();
      this.pruneDependentFilters();
      this.render({ updateHistory: false });
    }

    clearFilters() {
      this.state.filters.clear();
      this.state.minPrice = null;
      this.state.maxPrice = null;
      this.state.page = 1;
      this.render({ updateHistory: true });
    }

    removeFilter(key, value) {
      if (key === 'price') {
        this.state.minPrice = null;
        this.state.maxPrice = null;
      } else {
        const selected = this.state.filters.get(key);
        selected?.delete(value);
        if (!selected?.size) this.state.filters.delete(key);
      }
      this.state.page = 1;
      this.pruneDependentFilters();
      this.render({ updateHistory: true });
    }

    openDrawer(trigger) {
      this.drawerTrigger = trigger || this.root.querySelector('[data-facet-open]');
      this.root.classList.add('is-facet-drawer-open');
      document.body.classList.add('facets-drawer-open');
      this.drawerTrigger?.setAttribute('aria-expanded', 'true');
      requestAnimationFrame(() => this.root.querySelector('.native-facets__close')?.focus());
    }

    closeDrawer() {
      if (!this.root.classList.contains('is-facet-drawer-open')) return;
      this.root.classList.remove('is-facet-drawer-open');
      document.body.classList.remove('facets-drawer-open');
      this.root.querySelector('[data-facet-open]')?.setAttribute('aria-expanded', 'false');
      (this.drawerTrigger || this.root.querySelector('[data-facet-open]'))?.focus();
    }

    captureFacetAnchor(input) {
      const mobileGroups = this.root.querySelector('[data-facet-groups]');
      const scrollContainer = window.matchMedia('(max-width: 989px)').matches ? mobileGroups : this.sidebar;
      return {
        key: input.dataset.filterKey,
        value: input.value,
        visualOffset: input.closest('[data-facet-value]')?.getBoundingClientRect().top - scrollContainer.getBoundingClientRect().top,
        scrollContainer: scrollContainer || this.sidebar
      };
    }

    restoreFacetAnchor(anchor) {
      if (!anchor || !Number.isFinite(anchor.visualOffset) || !anchor.scrollContainer?.isConnected) return;
      const mobileDrawer = window.matchMedia('(max-width: 989px)').matches;
      const restore = () => {
        const input = Array.from(this.root.querySelectorAll('[data-custom-filter-input]')).find(candidate => (
          candidate.dataset.filterKey === anchor.key && candidate.value === anchor.value
        ));
        const item = input?.closest('[data-facet-value]');
        if (!item) return;
        if (mobileDrawer) this.sidebar.scrollTop = 0;
        const contentOffset = item.getBoundingClientRect().top - anchor.scrollContainer.getBoundingClientRect().top + anchor.scrollContainer.scrollTop;
        anchor.scrollContainer.scrollTop = Math.max(0, contentOffset - anchor.visualOffset);
        if (!mobileDrawer) input.focus({ preventScroll: true });
      };
      if (mobileDrawer) requestAnimationFrame(restore);
      else restore();
    }

    // A wall of shut drawers reads as "no filters here", so the first few
    // visible groups land open. Only the leading ones — a camera page offers
    // eight groups and opening them all pushes the grid below the fold.
    openLeadingGroups(count) {
      Array.from(this.root.querySelectorAll('[data-custom-facet-group]:not([hidden])'))
        .slice(0, count)
        .forEach(group => { group.open = true; });
    }

    visibleFacetKeys() {
      return Array.from(this.root.querySelectorAll('[data-custom-facet-group]:not([hidden])')).map(group => group.dataset.facetKey);
    }

    advanceFacet(fromKey, visibleBefore = []) {
      if (!fromKey) return;
      const current = this.root.querySelector(`[data-custom-facet-group][data-facet-key="${fromKey}"]`);
      if (!current || current.hidden) return;
      const visibleGroups = Array.from(this.root.querySelectorAll('[data-custom-facet-group]:not([hidden])'));
      const newlyVisible = visibleBefore.length
        ? visibleGroups.filter(group => !visibleBefore.includes(group.dataset.facetKey))
        : [];
      const currentIndex = visibleGroups.indexOf(current);
      const target = newlyVisible[0] || visibleGroups.slice(currentIndex + 1).find(group => group !== current);

      if (!target) return;
      target.open = true;
      target.classList.add('is-guided-target');

      requestAnimationFrame(() => {
        const mobile = window.matchMedia('(max-width: 989px)').matches;
        const scrollContainer = mobile ? this.root.querySelector('[data-facet-groups]') : this.sidebar;
        if (!scrollContainer) return;
        if (mobile) this.sidebar.scrollTop = 0;
        const contentOffset = target.getBoundingClientRect().top - scrollContainer.getBoundingClientRect().top + scrollContainer.scrollTop;
        scrollContainer.scrollTo({
          top: Math.max(0, contentOffset - 8),
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
        });
        window.setTimeout(() => target.classList.remove('is-guided-target'), 900);
      });
    }

    updatePriceSlider(priceForm) {
      const minRange = priceForm.querySelector('[data-price-range="min"]');
      const maxRange = priceForm.querySelector('[data-price-range="max"]');
      const slider = priceForm.querySelector('[data-price-slider]');
      if (!minRange || !maxRange || !slider) return;
      const ceiling = Number(maxRange.max) || 1;
      slider.style.setProperty('--price-min-position', `${Math.min(100, Math.max(0, Number(minRange.value) / ceiling * 100))}%`);
      slider.style.setProperty('--price-max-position', `${Math.min(100, Math.max(0, Number(maxRange.value) / ceiling * 100))}%`);
    }

    render(options = {}) {
      const matching = this.sortProducts(this.matchingProducts());
      const pages = Math.max(1, Math.ceil(matching.length / this.pageSize));
      this.state.page = Math.min(Math.max(1, this.state.page), pages);
      this.renderFacets();
      this.renderToolbarFilters();
      this.renderSummary(matching.length);
      this.renderActiveFacets();
      this.renderProducts(matching);
      this.renderPagination(matching.length);
      this.syncSortControls();
      this.syncFilterCount();
      this.syncQuickControls();
      this.restoreFacetAnchor(options.facetAnchor);
      this.advanceFacet(options.advanceFrom, options.visibleBefore);
      if (options.updateHistory) this.updateUrl();
      this.renderBreadcrumbs();
      this.renderCategorySeo();
      if (options.scrollToResults) this.scrollToResults();
    }

    // The category guide under the grid. Content lives in a theme asset keyed
    // by the active category; no entry means the container stays hidden, so
    // pages without a written guide look exactly as they did before.
    renderCategorySeo() {
      const host = document.querySelector('[data-category-seo]');
      if (!host) return;
      if (!this.categorySeoPromise) {
        const url = this.root.dataset.categorySeoUrl;
        this.categorySeoPromise = url
          ? fetch(url, { credentials: 'omit' }).then(res => (res.ok ? res.json() : null)).catch(() => null)
          : Promise.resolve(null);
      }
      this.categorySeoPromise.then(content => {
        const entry = content ? this.categorySeoEntry(content) : null;
        const schemaId = 'category-seo-schema';
        document.getElementById(schemaId)?.remove();
        if (!entry) {
          host.hidden = true;
          host.innerHTML = '';
          return;
        }
        const crumb = ['Home', ...(entry.crumb || [])];
        const sections = (entry.sections || []).map(section =>
          `<h3>${section.h3}</h3>${section.html}`
        ).join('');
        const table = entry.table
          ? `<h3>${entry.table.caption}</h3><table><thead><tr>${entry.table.head.map(cell => `<th>${cell}</th>`).join('')}</tr></thead><tbody>${entry.table.rows.map(row => `<tr>${row.map(cell => `<td>${cell}</td>`).join('')}</tr>`).join('')}</tbody></table>`
          : '';
        const faq = (entry.faq || []).length
          ? `<h3>Frequently asked questions</h3>${entry.faq.map((item, index) =>
              `<details${index === 0 ? ' open' : ''}><summary>${item.q}</summary><p>${item.a}</p></details>`
            ).join('')}`
          : '';
        const related = (entry.related || []).length
          ? `<div class="category-seo__related"><strong>Related categories:</strong> ${entry.related.map(link => `<a class="category-seo__pill" href="${link.href}">${link.label}</a>`).join('')}</div>`
          : '';
        // Category images are ordinary theme assets living beside this JSON,
        // so the content URL doubles as the CDN base for them.
        const assetBase = (this.root.dataset.categorySeoUrl || '').split('?')[0].replace(/[^/]+$/, '');
        const media = entry.image && assetBase
          ? `<figure class="category-seo__media"><img src="${assetBase}${entry.image}" alt="${entry.imageAlt || entry.h2}" loading="lazy">${entry.imageCaption ? `<figcaption>${entry.imageCaption}</figcaption>` : ''}</figure>`
          : '';
        host.innerHTML = `
          <div class="category-seo__main">
            <nav class="category-seo__crumbs" aria-label="Category">${crumb.join(' › ')}</nav>
            <h2>${entry.h2}</h2>
            ${entry.intro || ''}
            ${sections}
            ${table}
          </div>
          <div class="category-seo__aside">
            ${media}
            <div class="category-seo__faq">${faq}</div>
          </div>
          ${related}`;
        host.hidden = false;

        const schema = [];
        if ((entry.faq || []).length) {
          schema.push({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: entry.faq.map(item => ({
              '@type': 'Question',
              name: item.q,
              acceptedAnswer: { '@type': 'Answer', text: item.a.replace(/<[^>]+>/g, '') }
            }))
          });
        }
        schema.push({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: crumb.map((name, index) => ({
            '@type': 'ListItem', position: index + 1, name
          }))
        });
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.id = schemaId;
        script.textContent = JSON.stringify(schema);
        document.head.appendChild(script);
      });
    }

    // Most specific context wins: an explicit collection page, then a camera
    // form (with its resolution split), then a firewall tier, then the family,
    // then the area. A free-text search means the visitor has left the
    // category, so the guide steps aside. Entries may alias one another by
    // storing the target key as a string.
    categorySeoEntry(content) {
      if (this.state.query) return null;
      const single = key => {
        const values = this.state.filters.get(key);
        return values && values.size === 1 ? [...values][0] : null;
      };
      const candidates = [];
      if (this.isScopedCollection) {
        const handle = (this.root.dataset.collectionUrl || '').split('/collections/')[1];
        if (handle) candidates.push(`collection:${handle.split('?')[0]}`);
      }
      const form = single('cameraForm');
      if (form) {
        const resolutions = this.state.filters.get('cameraResolution');
        if (resolutions?.size) candidates.push(`form:${form}|res:${[...resolutions].sort().join('+')}`);
        candidates.push(`form:${form}`);
      }
      const tier = single('firewallTier');
      if (tier) candidates.push(`tier:${tier}`);
      const brand = single('brand');
      const family = single('family');
      // A brand filter narrows the page to one vendor's products, so a
      // brand-specific guide (if one exists) is a better match than the
      // generic family guide — which is written around whichever vendor
      // dominates that family and reads as wrong (wrong brand's photo/copy)
      // on a page scoped to a different vendor, e.g. brand::Fortinet +
      // family::Routers (FortiExtender) showing the Grandstream/Neolink
      // router guide. Brand is tried before family; family is still the
      // fallback when no brand-specific guide exists.
      if (brand && !form && !tier) candidates.push(`brand:${brand}`);
      if (family) candidates.push(`family:${family}`);
      const area = single('area');
      if (area && !family) candidates.push(`area:${area}`);
      for (const key of candidates) {
        let entry = content[key];
        if (typeof entry === 'string') entry = content[entry];
        if (entry) return entry;
      }
      return null;
    }

    syncQuickControls() {
      const toggle = this.root.querySelector('[data-instock-toggle]');
      if (toggle) {
        const active = this.state.filters.get('availability')?.has('In stock') || false;
        toggle.classList.toggle('is-active', active);
        toggle.setAttribute('aria-pressed', String(active));
      }
      const search = this.root.querySelector('[data-catalogue-search]');
      if (search && document.activeElement !== search && search.value.trim() !== (this.state.query || '')) {
        search.value = this.state.query || '';
      }
    }

    renderBreadcrumbs() {
      const nav = document.querySelector('[data-collection-breadcrumbs]');
      if (!nav) return;

      const homeUrl = nav.dataset.homeUrl || '/';
      const productsUrl = nav.dataset.productsUrl || '/collections/all';
      const homeLabel = nav.dataset.homeLabel || 'Home';
      const productsLabel = nav.dataset.productsLabel || 'Products';
      const crumbs = [
        { label: homeLabel, href: homeUrl },
        { label: productsLabel, href: productsUrl }
      ];
      const accumulated = new Map();
      const activeKeys = [
        ...BREADCRUMB_FACET_ORDER.filter(key => this.state.filters.get(key)?.size),
        ...[...this.state.filters.keys()].filter(key => !BREADCRUMB_FACET_ORDER.includes(key) && this.state.filters.get(key)?.size)
      ];

      activeKeys.forEach(key => {
        const values = [...this.state.filters.get(key)];
        if (!values.length) return;
        accumulated.set(key, values);
        const url = new URL(productsUrl, window.location.origin);
        accumulated.forEach((selectedValues, selectedKey) => {
          selectedValues.forEach(value => url.searchParams.append(`f_${selectedKey}`, value));
        });
        const sort = new URL(window.location.href).searchParams.get('sort_by');
        if (sort) url.searchParams.set('sort_by', sort);
        crumbs.push({ label: values.join(' + '), href: `${url.pathname}${url.search}` });
      });

      if (this.state.minPrice !== null || this.state.maxPrice !== null) {
        const min = this.state.minPrice !== null ? this.state.minPrice : 0;
        const max = this.state.maxPrice !== null ? this.state.maxPrice : 'Any';
        crumbs.push({ label: `Price ${min}–${max}`, href: window.location.pathname + window.location.search });
      }

      nav.innerHTML = crumbs.map((crumb, index) => {
        const isCurrent = index === crumbs.length - 1;
        const content = isCurrent
          ? `<span aria-current="page">${escapeHtml(crumb.label)}</span>`
          : `<a href="${escapeHtml(crumb.href)}">${escapeHtml(crumb.label)}</a>`;
        return `${index ? '<span class="collection-breadcrumbs__separator" aria-hidden="true">&gt;</span>' : ''}${content}`;
      }).join('');
    }

    renderFacets() {
      this.root.querySelectorAll('[data-custom-facet-group]').forEach(group => {
        const key = group.dataset.facetKey;
        if (key === 'availability' || key === 'price') {
          group.hidden = true;
          return;
        }
        const content = group.querySelector('[data-facet-content]');
        const countBadge = group.querySelector('[data-group-active-count]');
        const reopenLabel = group.querySelector('[data-facet-reopen-label]');
        const selectedCount = key === 'price'
          ? Number(this.state.minPrice !== null || this.state.maxPrice !== null)
          : (this.state.filters.get(key)?.size || 0);
        if (countBadge) {
          countBadge.hidden = selectedCount === 0;
          countBadge.textContent = selectedCount;
        }
        if (reopenLabel) reopenLabel.hidden = selectedCount === 0;

        if (!this.isFacetRelevant(key)) {
          group.hidden = true;
          return;
        }

        // The "Product categories" group is a way in, not a filter to live
        // with: once a category is chosen — by menu, by collection page or in
        // the group itself — it gives way to that category's own feature
        // facets. The active-filter chip above the grid clears it.
        if (key === 'family' && (this.isScopedCollection || selectedCount > 0)) {
          group.hidden = true;
          return;
        }

        if (key === 'price') {
          const prices = this.matchingProducts('price').flatMap(product => [product.priceMin, product.priceMax]);
          const maxPrice = prices.length ? Math.ceil(Math.max(...prices) / 100) : 0;
          group.hidden = maxPrice <= 0;
          if (!group.hidden) content.innerHTML = this.priceFilterMarkup(maxPrice);
          return;
        }

        const options = this.optionCounts(key).filter(option => option.count > 0 || option.active);
        const activeOptions = options.filter(option => option.active).length;
        // A facet whose remaining options cannot change the result set is
        // clutter — hide it unless a value is already selected.
        group.hidden = options.length === 0 || (options.length < 2 && activeOptions === 0);
        if (group.hidden) return;
        content.innerHTML = this.optionsMarkup(group, key, options);
      });

      const available = this.matchingProducts().length;
      const sidebarCount = this.root.querySelector('[data-facet-product-count]');
      const drawerCount = this.root.querySelector('[data-drawer-result-count]');
      if (sidebarCount) sidebarCount.textContent = `${available} available`;
      if (drawerCount) drawerCount.textContent = available;
    }

    renderToolbarFilters() {
      if (!this.toolbarFilters) return;
      const parts = [];
      const availabilityOptions = this.optionCounts('availability').filter(option => option.count > 0 || option.active);
      if (availabilityOptions.length) {
        const selected = this.state.filters.get('availability') || new Set();
        const selectedCount = selected.size;
        parts.push(`
          <details class="native-toolbar-filter" data-toolbar-filter data-toolbar-filter-key="availability">
            <summary class="native-toolbar-filter__summary">
              <span>Availability</span>
              ${selectedCount ? `<span class="native-toolbar-filter__count">${selectedCount}</span>` : ''}
              <span class="native-toolbar-filter__chevron" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="m6 9 6 6 6-6"></path></svg>
              </span>
            </summary>
            <div class="native-toolbar-filter__panel">
              <ul class="native-facet-values native-toolbar-filter__values">
                ${availabilityOptions.map((option, index) => {
                  const id = `ToolbarFacet-availability-${handleize(option.label)}-${this.root.dataset.sectionId}-${index}`;
                  return `
                    <li class="native-facet-value" data-facet-value data-facet-label="${escapeHtml(option.label.toLowerCase())}">
                      <label class="native-facet-checkbox" for="${id}">
                        <input id="${id}" type="checkbox" value="${escapeHtml(option.label)}" data-custom-filter-input data-filter-key="availability"${option.active ? ' checked' : ''}>
                        <span class="native-facet-checkbox__box" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><path d="m5 12 4 4L19 6"></path></svg></span>
                        <span class="native-facet-checkbox__label">${escapeHtml(option.label)}</span>
                        <span class="native-facet-checkbox__count">${option.count}</span>
                      </label>
                    </li>`;
                }).join('')}
              </ul>
            </div>
          </details>`);
      }

      const prices = this.matchingProducts('price').flatMap(product => [product.priceMin, product.priceMax]);
      const maxPrice = prices.length ? Math.ceil(Math.max(...prices) / 100) : 0;
      if (maxPrice > 0) {
        const selectedCount = Number(this.state.minPrice !== null || this.state.maxPrice !== null);
        parts.push(`
          <details class="native-toolbar-filter native-toolbar-filter--price" data-toolbar-filter data-toolbar-filter-key="price">
            <summary class="native-toolbar-filter__summary">
              <span>Price</span>
              ${selectedCount ? '<span class="native-toolbar-filter__count">1</span>' : ''}
              <span class="native-toolbar-filter__chevron" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="m6 9 6 6 6-6"></path></svg>
              </span>
            </summary>
            <div class="native-toolbar-filter__panel native-toolbar-filter__panel--price">
              ${this.priceFilterMarkup(maxPrice)}
            </div>
          </details>`);
      }

      this.toolbarFilters.hidden = parts.length === 0;
      this.toolbarFilters.innerHTML = parts.join('');
    }

    optionsMarkup(group, key, options) {
      const visible = Number(group.dataset.visibleValues) || this.defaultVisible;
      const searchable = group.dataset.enableSearch === 'true' && options.length > visible;
      const search = searchable ? `
        <label class="native-facet-search">
          <span class="native-facet-search__icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="7"></circle><path d="m20 20-4-4"></path></svg>
          </span>
          <input type="search" placeholder="Search options" autocomplete="off" data-facet-search>
        </label>` : '';
      const values = options.map((option, index) => {
        const collapsed = index >= visible && !option.active ? ' native-facet-value--collapsed' : '';
        const id = `CustomFacet-${handleize(key)}-${handleize(option.label)}-${this.root.dataset.sectionId}`;
        return `
          <li class="native-facet-value${collapsed}" data-facet-value data-facet-label="${escapeHtml(option.label.toLowerCase())}">
            <label class="native-facet-checkbox" for="${id}">
              <input id="${id}" type="checkbox" value="${escapeHtml(option.label)}" data-custom-filter-input data-filter-key="${escapeHtml(key)}"${option.active ? ' checked' : ''}>
              <span class="native-facet-checkbox__box" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><path d="m5 12 4 4L19 6"></path></svg></span>
              <span class="native-facet-checkbox__label">${escapeHtml(option.label)}</span>
              <span class="native-facet-checkbox__count">${option.count}</span>
            </label>
          </li>`;
      }).join('');
      const more = options.length > visible ? `
        <button type="button" class="native-facet-more" data-facet-more aria-expanded="false">
          <span data-facet-more-label>Show more</span><span aria-hidden="true">+</span>
        </button>` : '';
      return `${search}<ul class="native-facet-values" data-facet-values>${values}</ul>${more}<p class="native-facet-search-empty" data-facet-search-empty hidden>No matching options</p>`;
    }

    priceFilterMarkup(maxPrice) {
      const selectedMin = Math.min(maxPrice, Math.max(0, this.state.minPrice ?? 0));
      const selectedMax = Math.min(maxPrice, Math.max(selectedMin, this.state.maxPrice ?? maxPrice));
      const minPosition = maxPrice ? selectedMin / maxPrice * 100 : 0;
      const maxPosition = maxPrice ? selectedMax / maxPrice * 100 : 100;
      return `
        <form class="native-price-filter" data-custom-price-form>
          <p class="native-price-filter__range">Available range: ${this.formatMoney(0)} - ${this.formatMoney(maxPrice * 100)}</p>
          <div class="native-price-filter__slider" data-price-slider style="--price-min-position:${minPosition}%;--price-max-position:${maxPosition}%">
            <span class="native-price-filter__track" aria-hidden="true"><span></span></span>
            <input type="range" min="0" max="${maxPrice}" value="${selectedMin}" step="1" data-price-range="min" aria-label="Minimum price slider">
            <input type="range" min="0" max="${maxPrice}" value="${selectedMax}" step="1" data-price-range="max" aria-label="Maximum price slider">
          </div>
          <div class="native-price-filter__inputs">
            <label><span>Min</span><span class="native-price-filter__field"><span>${escapeHtml(this.root.dataset.currencySymbol)}</span><input type="number" name="price_min" min="0" max="${maxPrice}" value="${selectedMin}" inputmode="decimal" data-price-number="min"></span></label>
            <span class="native-price-filter__separator" aria-hidden="true">to</span>
            <label><span>Max</span><span class="native-price-filter__field"><span>${escapeHtml(this.root.dataset.currencySymbol)}</span><input type="number" name="price_max" min="0" max="${maxPrice}" value="${selectedMax}" inputmode="decimal" data-price-number="max"></span></label>
          </div>
          <button type="submit" class="native-price-filter__apply">Apply price</button>
        </form>`;
    }

    renderSummary(total) {
      const start = total ? (this.state.page - 1) * this.pageSize + 1 : 0;
      const end = Math.min(total, this.state.page * this.pageSize);
      const count = this.root.querySelector('[data-result-count]');
      const range = this.root.querySelector('[data-result-range]');
      if (count) count.textContent = `${total} ${total === 1 ? 'product' : 'products'}`;
      if (range) range.textContent = total ? `Showing ${start}-${end}` : 'No matching products';
    }

    renderActiveFacets() {
      const container = this.root.querySelector('[data-active-facets]');
      if (!container) return;
      const chips = [];
      const labels = new Map(this.config.blocks.map(block => [block.key, block.label]));
      // Keys that scope the page without a sidebar group still need a human
      // label on their removal chip, not a raw camelCase key.
      if (!labels.has('area')) labels.set('area', 'Business area');
      for (const [key, selected] of this.state.filters.entries()) {
        selected.forEach(value => chips.push({ key, value, label: `${labels.get(key) || key}: ${value}` }));
      }
      if (this.state.minPrice !== null || this.state.maxPrice !== null) {
        const min = this.state.minPrice === null ? this.formatMoney(0) : this.formatMoney(this.state.minPrice * 100);
        const max = this.state.maxPrice === null ? 'Any' : this.formatMoney(this.state.maxPrice * 100);
        chips.push({ key: 'price', value: '', label: `Price: ${min} - ${max}` });
      }
      container.hidden = chips.length === 0;
      container.innerHTML = chips.length ? `${chips.map(chip => `
        <button type="button" class="native-active-facet" data-remove-filter-key="${escapeHtml(chip.key)}" data-remove-filter-value="${escapeHtml(chip.value)}">
          <span>${escapeHtml(chip.label)}</span><span aria-hidden="true">x</span>
        </button>`).join('')}
        <button type="button" class="native-active-facets__clear" data-clear-filters>Clear all</button>` : '';
    }

    renderProducts(products) {
      const start = (this.state.page - 1) * this.pageSize;
      const visible = products.slice(start, start + this.pageSize);
      if (!visible.length) {
        this.grid.innerHTML = `
          <div class="native-empty-state">
            <span class="native-empty-state__icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><circle cx="11" cy="11" r="7"></circle><path d="m20 20-4-4M8.5 11h5"></path></svg></span>
            <strong>No products match these filters</strong>
            <p>Remove one or more filters to widen the results.</p>
            <button type="button" class="custom-empty-state__clear" data-clear-filters>Clear filters</button>
          </div>`;
        return;
      }
      this.grid.innerHTML = visible.map(product => this.productCardMarkup(product)).join('');
      this.refreshAvailability(visible);
    }

    // A product's live stock, variant by variant, in the four tiers. The
    // stock view (templates/product.stock.liquid) knows the quantities; the
    // storefront's .js only says orderable or not, so it is the fallback, and
    // it never turns a back-ordered line into "In stock".
    async liveStock(product) {
      try {
        const response = await fetch(`/products/${encodeURIComponent(product.handle)}?view=stock`, { credentials: 'same-origin' });
        if (response.ok) {
          const payload = await response.json();
          const variants = Array.isArray(payload?.variants) ? payload.variants : [];
          if (variants.length && variants.every(v => STOCK_TIERS.includes(v.state))) {
            return { variants: variants.map(v => ({ id: v.id, state: v.state })) };
          }
        }
      } catch (_) {
        // The stock view is missing or answered something else: use .js.
      }
      try {
        const response = await fetch(`/products/${encodeURIComponent(product.handle)}.js`, { credentials: 'same-origin' });
        if (!response.ok) return null;
        const live = await response.json();
        const known = new Map((product.colours || []).map(entry => [String(entry.id), entry.stock]));
        const variants = (live?.variants || []).map(v => {
          const was = known.get(String(v.id)) || product.stock;
          return { id: v.id, state: !v.available ? 'out' : was === 'back' ? 'back' : was === 'low' ? 'low' : 'in' };
        });
        return variants.length ? { variants } : null;
      } catch (_) {
        return null;
      }
    }

    // The index the grid is drawn from can be up to two days old (the nightly
    // asset) or ten minutes old (the session cache), while stock moves every
    // hour. So the cards on screen ask the storefront for their product's
    // live availability and correct the badge and the button — the page a
    // customer opens and the card that led there must never disagree.
    async refreshAvailability(visible) {
      const token = (this.availabilityToken = (this.availabilityToken || 0) + 1);
      const queue = visible.filter(product => product.handle);
      const worker = async () => {
        while (queue.length) {
          const product = queue.shift();
          const live = await this.liveStock(product);
          if (!live) continue;
          if (token !== this.availabilityToken) return;
          const liveVariants = live.variants;
          product.stock = bestStock(liveVariants.map(v => v.state));
          product.available = isInStock(product.stock);
          product.orderable = product.stock !== 'out';
          if (Array.isArray(product.facets?.availability)) product.facets.availability = [AVAILABILITY_LABEL[product.stock]];
          if (Array.isArray(product.colours)) {
            product.colours.forEach(entry => {
              const own = liveVariants.find(v => String(v.id) === String(entry.id));
              if (own) Object.assign(entry, { stock: own.state, available: isInStock(own.state), orderable: own.state !== 'out' });
            });
          }
          const card = this.grid.querySelector(`[data-product-handle="${CSS.escape(product.handle)}"]`);
          if (!card) continue;
          // A card showing one colour (a colour filter is on) speaks for that
          // colour; any other card for the product as a whole.
          const pinned = card.dataset.pinnedVariant || '';
          const variant = pinned
            ? liveVariants.find(v => String(v.id) === pinned)
            : liveVariants.find(v => isInStock(v.state)) || liveVariants.find(v => v.state !== 'out') || liveVariants[0];
          const stock = pinned ? (variant?.state || 'out') : product.stock;
          const available = isInStock(stock);
          const orderable = stock !== 'out';
          const badge = card.querySelector('.product-card__availability');
          if (badge) {
            badge.textContent = STOCK_BADGE[stock];
            badge.className = `product-card__availability${available ? '' : ' product-card__availability--out'} product-card__availability--${stock}`;
          }
          const form = card.querySelector('form.product-card__form');
          const button = form?.querySelector('button.product-card__add');
          if (button) {
            button.disabled = !orderable;
            button.textContent = orderable ? 'Add to basket' : 'Sold out';
          }
          const idInput = form?.querySelector('input[name="id"]');
          if (idInput && variant?.id) idInput.value = variant.id;
        }
      };
      await Promise.all(Array.from({ length: Math.min(6, queue.length) }, worker));
    }

    // The colour a card should show when a colour filter is on: the first
    // selected colour the product comes in, preferring one in stock.
    pickedColour(product) {
      const colours = Array.isArray(product.colours) ? product.colours : [];
      if (colours.length < 2) return null;
      const selected = this.state.filters.get('cableColour');
      if (!selected || !selected.size) return null;
      const matches = colours.filter(entry => selected.has(entry.colour));
      return matches.find(entry => entry.available) || matches.find(entry => entry.orderable) || matches[0] || null;
    }

    productCardMarkup(product) {
      const colours = Array.isArray(product.colours) ? product.colours : [];
      const picked = this.pickedColour(product);
      const choosesColour = colours.length > 1 && !picked;
      const baseUrl = `/products/${encodeURIComponent(product.handle)}`;
      const url = picked ? `${baseUrl}?variant=${encodeURIComponent(picked.id)}` : baseUrl;
      const stock = (picked ? picked.stock : product.stock) || ((picked ? picked.available : product.available) ? 'in' : 'out');
      const available = isInStock(stock);
      const orderable = stock !== 'out';
      const cardImage = (picked && picked.image) || product.image;
      const sku = picked ? picked.sku : choosesColour ? '' : product.sku;
      const variantId = picked ? picked.id : product.variantId;
      const locked = this.root.dataset.customerLoggedIn !== 'true' && hasRestrictedPriceBrand(product);
      const reseller = this.root.dataset.customerReseller === 'true';
      const quoteLabel = this.root.dataset.quoteButtonLabel || 'Get quote';
      const simQuoteUrl = this.root.dataset.simQuoteUrl || '';
      const fortinetQuoteUrl = this.root.dataset.fortinetQuoteUrl || '';
      const fortinetQuoteThreshold = Number(this.root.dataset.fortinetQuoteThreshold || 0);
      const needsSimQuote = !locked && isSimQuoteProduct(product);
      const needsFortinetQuote = !locked && isFortinetQuoteProduct(product, fortinetQuoteThreshold);
      const image = cardImage
        ? `<img src="${escapeHtml(cardImage)}" alt="${escapeHtml(picked ? `${product.title} — ${picked.colour}` : product.title)}" loading="lazy" width="600" height="535">`
        : '<div class="placeholder custom-product-placeholder"></div>';
      const sale = product.compareAtPrice > product.priceMin && available ? '<span class="product-card__badge">Sale</span>' : '';
      const price = locked ? `
        <div class="product-card__price price-lock price-lock--locked">
          <span class="price-lock__message price-lock__message--quiet">Trade pricing</span>
        </div>` : isPriceOnRequest(product) ? `
        <div class="product-card__price price-lock">
          <span class="price-lock__message">Price on request</span>
        </div>` : this.productPriceMarkup(product, reseller);
      const quoteForm = needsSimQuote
        ? `<div class="product-card__form">${simQuoteUrl
          ? `<a href="${escapeHtml(simQuoteUrl)}" class="product-card__add product-card__add--quote" target="_blank" rel="noopener">${escapeHtml(quoteLabel)}</a>`
          : `<button type="button" class="product-card__add product-card__add--quote product-card__add--disabled" disabled>${escapeHtml(quoteLabel)}</button>`}</div>`
        : needsFortinetQuote
          ? `<div class="product-card__form">${fortinetQuoteUrl
            ? `<a href="${escapeHtml(fortinetQuoteUrl)}" class="product-card__add product-card__add--quote" target="_blank" rel="noopener">${escapeHtml(quoteLabel)}</a>`
            : `<button type="button" class="product-card__add product-card__add--quote product-card__add--disabled" disabled>${escapeHtml(quoteLabel)}</button>`}</div>`
          : '';
      const form = locked ? `
        <div class="product-card__form"><a href="${escapeHtml(this.root.dataset.accountLoginUrl)}" class="product-card__add product-card__add--login">Sign in to buy</a></div>` : quoteForm || (choosesColour ? `
        <div class="product-card__form"><a href="${url}" class="product-card__add product-card__add--choose">Choose colour</a></div>` : `
        <form method="post" action="${escapeHtml(this.root.dataset.cartAddUrl)}" class="product-card__form">
          <input type="hidden" name="id" value="${escapeHtml(variantId)}">
          <button type="submit" class="product-card__add"${orderable ? '' : ' disabled'}>${orderable ? 'Add to basket' : 'Sold out'}</button>
        </form>`);
      const colourNote = picked
        ? `<span class="product-card__sku">${escapeHtml(picked.colour)}${sku ? ` · ${escapeHtml(sku)}` : ''}</span>`
        : choosesColour
          ? `<span class="product-card__sku">${colours.length} colours</span>`
          : sku ? `<span class="product-card__sku">${escapeHtml(sku)}</span>` : '';

      return `
        <div class="product-card" data-product-card data-product-handle="${escapeHtml(product.handle)}" data-product-vendor="${escapeHtml(handleize(product.brand))}"${picked ? ` data-pinned-variant="${escapeHtml(picked.id)}"` : ''}>
          <a href="${url}" class="product-card__media">${image}${sale}<span class="product-card__availability${available ? '' : ' product-card__availability--out'} product-card__availability--${stock}">${STOCK_BADGE[stock]}</span></a>
          <div class="product-card__info">
            <div class="product-card__meta"><span class="product-card__vendor">${escapeHtml(product.vendor || product.brand)}</span>${colourNote}</div>
            <h3 class="product-card__title"><a href="${url}">${escapeHtml(product.title)}</a></h3>
            <div class="product-card__benefits"><span>Trade support</span><span>UK dispatch</span></div>
            ${price}${form}
          </div>
        </div>`;
    }

    productPriceMarkup(product, reseller) {
      const discount = reseller ? 0.95 : 1;
      const min = Math.round(product.priceMin * discount);
      const max = Math.round(product.priceMax * discount);
      const main = min !== max ? `${this.formatMoney(min)} - ${this.formatMoney(max)}` : this.formatMoney(min);
      const compare = !reseller && product.compareAtPrice > product.priceMin
        ? `<span class="price-lock__compare">${this.formatMoney(product.compareAtPrice)}</span>` : '';
      const resellerNote = reseller ? `<span class="price-lock__note">Reseller price - 5% off</span><span class="price-lock__was">Regular ${product.priceMin !== product.priceMax ? `${this.formatMoney(product.priceMin)} - ${this.formatMoney(product.priceMax)}` : this.formatMoney(product.priceMin)}</span>` : '';
      return `<div class="product-card__price price-lock${reseller ? ' price-lock--reseller' : ''}" data-product-price><span class="price-lock__main"><span>${main}</span></span><span class="price-lock__tax-label">Exc VAT</span>${resellerNote}${compare}</div>`;
    }

    formatMoney(cents) {
      return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format((Number(cents) || 0) / 100);
    }

    sortProducts(products) {
      const output = [...products];
      const sort = this.state.sort;
      if (sort === 'title-ascending') output.sort((a, b) => a.title.localeCompare(b.title, undefined, { numeric: true }));
      else if (sort === 'title-descending') output.sort((a, b) => b.title.localeCompare(a.title, undefined, { numeric: true }));
      else if (sort === 'price-ascending') output.sort((a, b) => a.priceMin - b.priceMin || a.title.localeCompare(b.title));
      else if (sort === 'price-descending') output.sort((a, b) => b.priceMax - a.priceMax || a.title.localeCompare(b.title));
      else if (sort === 'created-descending') output.sort((a, b) => b.createdAt - a.createdAt);
      else if (sort === 'created-ascending') output.sort((a, b) => a.createdAt - b.createdAt);
      else output.sort((a, b) => a.rank - b.rank);
      return output;
    }

    renderPagination(total) {
      const pageCount = Math.ceil(total / this.pageSize);
      if (pageCount <= 1) {
        this.pagination.innerHTML = '';
        return;
      }
      const current = this.state.page;
      const pages = unique([1, current - 1, current, current + 1, pageCount].filter(page => page >= 1 && page <= pageCount)).sort((a, b) => a - b);
      const parts = [];
      let previous = 0;
      pages.forEach(page => {
        if (previous && page - previous > 1) parts.push('<span class="custom-pagination__ellipsis">...</span>');
        parts.push(`<button type="button" class="custom-pagination__button${page === current ? ' is-current' : ''}" data-custom-page="${page}"${page === current ? ' aria-current="page"' : ''}>${page}</button>`);
        previous = page;
      });
      this.pagination.innerHTML = `<nav class="pagination custom-pagination" aria-label="Pagination">
        <button type="button" class="custom-pagination__button" data-custom-page="${current - 1}"${current === 1 ? ' disabled' : ''}>Previous</button>
        ${parts.join('')}
        <button type="button" class="custom-pagination__button" data-custom-page="${current + 1}"${current === pageCount ? ' disabled' : ''}>Next</button>
      </nav>`;
    }

    syncSortControls() {
      this.root.querySelectorAll('[data-custom-sort]').forEach(select => {
        select.value = this.state.sort;
        if (select.selectedIndex === -1) select.value = 'featured';
      });
    }

    syncFilterCount() {
      let count = 0;
      this.state.filters.forEach(values => { count += values.size; });
      if (this.state.minPrice !== null || this.state.maxPrice !== null) count += 1;
      const badge = this.root.querySelector('[data-filter-count]');
      if (badge) {
        badge.hidden = count === 0;
        badge.textContent = count;
      }
      this.root.dataset.activeFilterCount = String(count);
    }

    updateUrl() {
      const url = new URL(this.root.dataset.collectionUrl, window.location.origin);
      for (const [key, selected] of this.state.filters.entries()) {
        selected.forEach(value => url.searchParams.append(`f_${key}`, value));
      }
      if (this.state.minPrice !== null) url.searchParams.set('price_min', String(this.state.minPrice));
      if (this.state.maxPrice !== null) url.searchParams.set('price_max', String(this.state.maxPrice));
      if (this.state.sort && this.state.sort !== 'featured') url.searchParams.set('sort_by', this.state.sort);
      if (this.state.page > 1) url.searchParams.set('page', String(this.state.page));
      window.history.pushState({}, '', url.toString());
    }

    scrollToResults() {
      const toolbar = this.root.querySelector('[data-results-toolbar]');
      if (!toolbar) return;
      const headerOffset = Number.parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-bottom')) || 0;
      const top = toolbar.getBoundingClientRect().top + window.scrollY - headerOffset - 16;
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    }
  }

  const catalogueManager = {
    instances: [],
    mount() {
      this.instances = this.instances.filter(instance => instance.root.isConnected);
      document.querySelectorAll('[data-custom-catalogue]').forEach(root => {
        if (root.dataset.dynamicCatalogueMounted === 'true') return;
        root.dataset.dynamicCatalogueMounted = 'true';
        this.instances.push(new DynamicCatalogue(root));
      });
    }
  };

  window.CliDynamicCatalogue = catalogueManager;
  catalogueManager.mount();
  document.addEventListener('shopify:section:load', () => catalogueManager.mount());
})();

